import { useState, useCallback, useRef, useMemo, useEffect, type JSX } from 'react';
// Day 76 P5.1 Phase 9B — PATH_LABEL retired; per-path labels derive from
// existing pathLabel useMemo (fpc.display_name via useSettingsConfig).
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type ColDef,
  type CellStyle,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type IGetRowsParams,
  type RowClickedEvent,
  type ValueFormatterParams,
  type ValueGetterParams,
} from 'ag-grid-community';
import {
  RefreshCw,
  AlertCircle,
  Search,
  ToggleLeft,
  ToggleRight,
  TrendingUp,
  X,
  Copy,
  Check,
  Columns3,
  Save,
  Trash2,
  ShoppingCart,
  Plus,
  Minus,
  ChevronDown,
} from 'lucide-react';
import clsx from 'clsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { Json } from '../lib/database.types';
import { SELECTION_COLUMN_DEF, emptyFormatter } from '../lib/gridUtils';
import { useApp } from '../hooks/useApp';
import { useDebugPanel } from '../hooks/useDebugPanel';
import { useSettingsConfig } from '../hooks/useSettingsConfig';
import { usePOStaging, type AddPOStagingResult } from '../hooks/usePOStaging';
import { aggregateMultiAddOutcome } from '../lib/multiAddAggregator';
import { escapeOrIlikeTerm } from '../lib/postgrestEscape';
import { resolveTooltip } from '../lib/bswcTooltipFallback';
import type {
  CalcProfitabilitySalePriceSource,
  CalcProfitabilityVelocitySource,
} from '../lib/engineFlags';
import { ProvenanceTooltip } from '../components/ui/ProvenanceTooltip';
import type { ProvenanceMap } from '../components/ui/ProvenanceIndicator';
import {
  FILTER_FIELDS,
  CONSTRAINT_FIELDS,
  DEFAULT_RISK_CONFIG,
  computeParamDefaults,
  buildSaveDefaultsPayload,
  mergePrefs,
  buildParamSnapshot,
} from '../lib/buyingSessionDefaults';
import {
  buildBuyingSessionPredicates,
  buildCrossChannelProfitEmbeds,
  normalizeDemandThreshold,
  DEMAND_NO_MATCH_SENTINEL,
} from '../lib/buyingSessionPredicates';
import {
  filterScenarioToCurrentKeys,
  scenarioMatchesState,
  type ValidKeySets,
} from '../lib/scenarioCleanup';
import { useParamStore } from '../hooks/useParamStore';
import {
  useBuyingSession,
  type BuyingSessionUiState,
  type BuyingSessionTierMode,
} from '../hooks/useBuyingSession';
import TierConfigModal from '../components/buying/TierConfigModal';
import { useScenarios } from '../hooks/useScenarios';
import { useSessionTabs } from '../hooks/useSessionTabs';
import { triggerTicker } from '../lib/activityTicker';
import { reportDatasourceFailure } from '../lib/agGridErrorTracker';
import ParamsSidebar from '../components/buying/ParamsSidebar';
import DetailPanel from '../components/buying/DetailPanel';
import POTray from '../components/buying/POTray';
import DistributorSelector from '../components/ui/DistributorSelector';
import ArbiSelect from '../components/ui/ArbiSelect';
import { MapWarningChip } from '../components/ui/MapWarningChip';
import { Toast, type ToastState } from '../components/ui/Toast';
import {
  computeSidebarChips,
  computeManualChips,
  type FilterChip,
} from '../components/buying/chipRow';
import type { FulfillmentPath, BuyingSessionPrefs, BuyingSessionRiskPref } from '../types';
import type { RiskFactorKey } from '../lib/poConstraints';
import {
  applyIrmSort,
  irmRangeBounds,
  runIrmGetRows,
  type IrmSortSpec,
} from '../lib/irmDatasource';

// Register AG Grid community modules
ModuleRegistry.registerModules([AllCommunityModule]);

// ── Local types ─────────────────────────────────────────────────────────────

type PathSelection = 'all' | FulfillmentPath;
type FilterMode = 'all' | 'profitable' | 'meets_roi';
type SortKey = 'roi_pct' | 'profit_per_unit' | 'profit_per_day' | 'roi_per_day' | 'buy_score';
type WeightAction = 'toggle' | 'weight';

// ── DD-19 (U10 slice A) — IRM sort specs ────────────────────────────────────
// Server-sortable columns per grid mode. The translation itself (remap →
// allowlist → ORDER BY, fallback, range bounds, success/fail contract) is the
// shared irmDatasource core; predicates and projections stay page-owned in
// getRows below.

// Cross-channel mode sorts on real products-table fields only. U10 cost
// re-point (:317 family): cost_usd left the allowlist with the read-copy —
// cost now rides the is_primary junction embed (to-many; not server-orderable
// without DDL), so the Cost header sort is disabled in all-mode (see the
// all-mode ColDef) instead of silently falling back.
const CROSS_CHANNEL_SORT: IrmSortSpec = {
  allowed: new Set([
    'isg_sku',
    'title',
    'brand',
    'upc',
    'asin',
    'amazon_demand_rating',
    'updated_at',
  ]),
  fallback: { column: 'isg_sku', ascending: true },
};

// Single-channel mode sorts on product_profitability fields.
const SINGLE_CHANNEL_SORT_COLUMNS: ReadonlySet<string> = new Set([
  'roi_pct',
  'profit_per_unit',
  'sale_price',
  'total_cost_per_unit',
  'effective_floor',
  'profit_per_day_safety',
  'profit_per_day_velocity',
  'roi_per_day_safety',
  'roi_per_day_velocity',
  'days_credit_locked_safety',
  'days_credit_locked_velocity',
  'buy_score',
]);

// Synthetic colIds (days_locked / profit_per_day / roi_per_day) switch between
// their _safety / _velocity DB siblings based on the safety-cap toggle,
// mirroring the valueGetters that paint the cells.
function remapSafetyCapColId(colId: string, safetyCap: boolean): string {
  if (colId === 'days_locked')
    return safetyCap ? 'days_credit_locked_safety' : 'days_credit_locked_velocity';
  if (colId === 'profit_per_day')
    return safetyCap ? 'profit_per_day_safety' : 'profit_per_day_velocity';
  if (colId === 'roi_per_day') return safetyCap ? 'roi_per_day_safety' : 'roi_per_day_velocity';
  return colId;
}

interface PathTab {
  id: PathSelection;
  label: string;
  short: string;
}

interface WeightState {
  enabled: boolean;
  weight: string;
}

// FilterChip + chip compute helpers extracted to src/components/buying/chipRow.ts
// (Day 81.5 P2.F2 — see audit F2 + Day 79 tierValidation.ts precedent).

interface ColumnPreset {
  name: string;
  hidden: string[];
}

interface ToggleableCol {
  id: string;
  label: string;
}

interface BuyingSessionProduct {
  id: string;
  isg_sku?: string | null;
  title?: string | null;
  brand?: string | null;
  cost_usd?: number | null;
  upc?: string | null;
  asin?: string | null;
  image_url?: string | null;
  weight_lbs?: number | null;
  length_in?: number | null;
  width_in?: number | null;
  height_in?: number | null;
  amazon_demand_rating?: string | null;
  amazon_monthly_sales?: number | null;
  amazon_seller_count?: number | null;
  algopix_recommendation?: string | null;
  // Day 64 F55: dedicated listing URL columns; algopix_data retained for fallback.
  amazon_listing_url?: string | null;
  ebay_listing_url?: string | null;
  walmart_listing_url?: string | null;
  algopix_data?: Record<string, unknown> | null;
  // Day 76 P5.1 Phase 6 — MAP UI surface (ADR 41 Shape B).
  // map_price from products.map_price; brands embed via PostgREST nested
  // SELECT carries name + enforces_map for tooltip source attribution.
  map_price?: number | null;
  brands?: { name?: string | null; enforces_map?: boolean | null } | null;
  // U10 cost re-point — the is_primary junction embed replaces the
  // products.cost_usd read-copy select (slice-B drop family).
  product_distributors?: Array<{ cost_usd: number | null; is_primary: boolean | null }> | null;
}

interface ChannelProfitRow {
  product_id?: string;
  /** pp's stored column (public.marketplace enum — carries fpc path keys post-U9;
   *  the stored name renames only when the enum dies, slice-B family). */
  marketplace?: string;
  no_price_data?: boolean | null;
  is_profitable?: boolean | null;
  meets_min_roi?: boolean | null;
  sale_price?: number | null;
  total_cost_per_unit?: number | null;
  marketplace_fees?: number | null;
  returns_cost?: number | null;
  profit_per_unit?: number | null;
  roi_pct?: number | null;
  margin_pct?: number | null;
  effective_floor?: number | null;
  profit_per_day_safety?: number | null;
  profit_per_day_velocity?: number | null;
  roi_per_day_safety?: number | null;
  roi_per_day_velocity?: number | null;
  days_credit_locked_safety?: number | null;
  days_credit_locked_velocity?: number | null;
  buy_score?: number | null;
  monthly_sales?: number | null;
  returns_rate?: number | null;
  // Day 76 P5.1 Phase 6 — MAP UI surface (ADR 41 Shape B). 3-state per
  // schema.sql COMMENT on product_profitability.below_map: NULL = engine
  // hasn't evaluated; TRUE = sale_price < map_price under enforcing brand;
  // FALSE = evaluated clean. UI gates chip render on === true only.
  below_map?: boolean | null;
  // Day 84.8 P1 — engine-side per-row source attribution flags per
  // src/lib/engineFlags.ts. Optional because pre-Day-84 cache rows
  // predate the columns; engine populates on next recalc.
  sale_price_source?: CalcProfitabilitySalePriceSource | null;
  velocity_source?: CalcProfitabilityVelocitySource | null;
  [key: string]: unknown;
}

interface AllChannelsRow extends BuyingSessionProduct {
  _channels: Record<string, ChannelProfitRow>;
  _isAllChannels: true;
}

interface SingleChannelRow extends ChannelProfitRow {
  products: BuyingSessionProduct;
}

type GridRow = AllChannelsRow | SingleChannelRow;

function isAllChannelsRow(row: GridRow | null | undefined): row is AllChannelsRow {
  return !!row && (row as AllChannelsRow)._isAllChannels === true;
}

interface AddToPOPayload {
  productId: string;
  isgSku: string | null | undefined;
  title: string | null | undefined;
  brand: string | null | undefined;
  unitCost: number;
  allocations: Record<string, string>;
}

/** PO Impact engine compute result (Day 72 P3.5; U10 re-point →
 *  calculate_po_impact_regrounded, the U9 S4 successor).
 *  Replaces client-side best-channel compute at L802-842. Per-path is sparse
 *  (only paths with non-zero allocated units). ROI% uses raw-cost denominator
 *  per Q4 lean (a) — see migration COMMENT for the documented divergence
 *  from engine roi_pct column (landed-cost denominator). */
interface POImpactPerPath {
  units: number;
  /** U10 re-point (calculate_po_impact_regrounded): kind-4 aggregates — NULL
   *  when any contributing unit's basis is unknown; unknown_units carries the
   *  legibility count (never a silent 0). */
  unknown_units: number;
  raw_cost: number | null;
  landed_cost: number | null;
  profit: number | null;
  roi_pct: number | null;
}

interface POImpactResult {
  distributor_id: string;
  user_id: string | null;
  items: number;
  /** Kind-4 top-level aggregates (see POImpactPerPath). */
  unknown_units: number;
  cost: number | null;
  est_profit: number | null;
  roi_pct: number | null;
  per_path: Record<string, POImpactPerPath>;
  safety_cap_mode: boolean;
  staging_row_count: number;
  /**
   * Day 81 P5.6 (F52) — count of staged rows excluded by the cross-
   * distributor WHERE filter (distributor_id IS NOT NULL AND <>
   * p_distributor_id). NULL distributor_id rows are charity-counted-IN
   * and do NOT contribute to this number. Always present in the RPC
   * return (default 0); UI gates the disclaimer render on > 0.
   */
  cross_distributor_excluded_count: number;
  computed_at: string;
}

/** A single line in the multi-add modal. Built from one AG Grid selected row.
 *  `roiByPath` caches the per-channel ROI so the displayed ROI% updates reactively
 *  when the user switches the path selector in All Channels mode. */
interface MultiAddDraft {
  productId: string;
  isgSku: string | null;
  title: string | null;
  cost: number;
  roiByPath: Record<string, number | null>;
  quantity: number;
  wasExisting: boolean;
}

interface StateRefShape {
  distributorId: string | null;
  selectedPath: PathSelection;
  filterMode: FilterMode;
  searchText: string;
  safetyCap: boolean;
  filters: Record<string, string | undefined>;
  /** Buying constraints — boolean entries store 'true'/'' (e.g. my_products_only). */
  constraints: Record<string, string | undefined>;
  /** Auth uid — needed for the my_products_only constraint predicate. */
  currentUserId: string | null;
  selectedBrand: string | null;
  onlyBranded: boolean;
  matchedBrandsOnly: boolean;
  brandList: string[];
  /** Pre-flattened brand-name list for the selected manufacturers. Empty
   *  when no manufacturer filter is active. */
  manufacturerBrandNames: string[];
  /** Day 58 — tier mode session config. import_id filter routes to the
   *  single-channel datasource branch when enabled+import_id is set. */
  tierMode: BuyingSessionTierMode;
  /** Day 91b F58 — persisted sort column for AG Grid applyColumnState on
   *  grid mount. Read in onGridReady to fire applyColumnState when
   *  hydration-written activeSort is non-null at first mount. */
  activeSort: SortKey | null;
}

/** Day 58 — tier RPC result row keyed by product_id.
 *  Day 69 (P3.2) additions:
 *    - Product-level: sale_price, map_price, below_map. Tier-invariant by
 *      construction; below_map per ADR 41 Shape B. Land dormant for current
 *      consumer (only roi_pct + ppu read today); Day 76 P5.1 MAP UI surfaces. */
interface TierResultByProduct {
  sale_price: number | null;
  map_price: number | null;
  below_map: boolean;
  by_tier: Array<{
    tier_index: number;
    qty_min: number;
    qty_max: number | null;
    unit_cost: number;
    ppu: number | null;
    roi_pct: number | null;
    margin_pct: number | null;
    profit_per_day_velocity: number | null;
    profit_per_day_safety: number | null;
    roi_per_day_velocity: number | null;
    roi_per_day_safety: number | null;
  }>;
}

interface ManufacturerEntry {
  id: string;
  name: string;
  /** Brand names assigned to this manufacturer — used as a direct
   *  `products.brand IN (...)` filter in the datasource. */
  brandNames: string[];
}

interface BrandRow {
  name: string | null;
}

interface RecalculateResult {
  calculated_at?: string;
  rows_upserted?: number;
  distributor_id?: string;
}

// ── Fulfillment-path tabs including "All Channels" ──────────────────────────

// TABS + label maps are built inside the component from
// useSettingsConfig().activePaths. Day 74 P4.2 — Hoisted PATH_LABEL_FALLBACK
// covers the ~60ms cache miss window before activePaths populates (Day
// 60b *_FALLBACK pattern; consumed at the multi-add modal title only).
// Per-path columns + dot iteration use activePaths directly with
// path.short_label inline — they render zero entries during cache miss
// and re-render with N entries once activePaths populates, so no short
// fallback const is needed.
const PATH_LABEL_FALLBACK: Record<string, string> = {
  amazon_fba: 'Amazon FBA',
  amazon_fbm: 'Amazon FBM',
  ebay: 'eBay',
  walmart_self: 'Walmart Self',
  walmart_wfs: 'Walmart WFS',
  shopify: 'Shopify',
};

// Day 60.5 — `marketplaceToPath` converter deleted; po_staging /
// po_line_items / po_line_item_allocations store the canonical long form
// (amazon_fba / amazon_fbm / ebay / walmart_self / walmart_wfs / shopify).
// U10 W1 re-point: those keys ARE fpc.path_key values — the U5 fulfillment
// entities are the vocabulary of record; the stored public.marketplace enum
// merely mirrors them until it dies (slice-B family). Path tab ids and
// fulfillment_path values are identity-equivalent.

// ── Formatting ───────────────────────────────────────────────────────────────

function fmt$(v: number | string | null | undefined): string {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  // Day 76 P5.1 Phase 9D — thousands separator + 2 decimals per
  // DATA_STANDARDS "$ prefix, 2 decimal places, thousands separator (,)".
  // Closes Day 47.5 BACKLOG follow-up "BuyingSession COST column truncating
  // decimals" (e.g. ISG-000001 was rendering "$1198" without separator;
  // now "$1,198.85"). Helper-level fix improves Sale Price + Profit/Unit
  // + Profit/Day + Cost + all other currency columns in the grid.
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtPct(v: number | string | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `${Number(v).toFixed(1)}%`;
}
function fmtInt(v: number | string | null | undefined): string {
  if (v === null || v === undefined) return '\u2014';
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return '\u2014';
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}
function fmtDays(v: number | string | null | undefined): string {
  if (v === null || v === undefined) return '—';
  const d = Number(v);
  return d < 1 ? '<1d' : `${Math.round(d)}d`;
}

function relativeTime(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return String(dateStr);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ── Module-scope SKU copy cells (need useState → must be real components) ───

interface SkuCopyButtonProps {
  sku: string | null | undefined;
}

function SkuCopyButton({ sku }: SkuCopyButtonProps): JSX.Element | null {
  const [copied, setCopied] = useState<boolean>(false);
  if (!sku) return null;
  return (
    <button
      type="button"
      aria-label={copied ? 'ISG SKU copied' : `Copy ISG SKU ${sku}`}
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(sku);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="flex items-center gap-1 font-mono text-accent hover:text-accent-hover transition-colors cursor-pointer"
      title={copied ? 'Copied!' : 'Click to copy'}
    >
      {sku}
      {copied ? (
        <Check size={10} className="text-success" />
      ) : (
        <Copy size={10} className="text-ink-muted opacity-0 group-hover:opacity-100" />
      )}
    </button>
  );
}

function SkuCellAllChannels(p: ICellRendererParams<GridRow>): JSX.Element | null {
  const data = p.data;
  if (!data || !isAllChannelsRow(data)) return null;
  return <SkuCopyButton sku={data.isg_sku} />;
}

function SkuCellSingleChannel(p: ICellRendererParams<GridRow>): JSX.Element | null {
  const data = p.data;
  if (!data || isAllChannelsRow(data)) return null;
  return <SkuCopyButton sku={data.products?.isg_sku} />;
}

// ── Main BuyingSession page ──────────────────────────────────────────────────

// ── Default export: URL-aware session router ────────────────────────────────
// When ?session=<uuid> is present, mount BuyingSessionContent keyed by the
// uuid (so React unmounts + remounts on session switch — clean state
// isolation). When the param is absent, render the Start Session landing
// card. Day 54 architecture per ADR Decision 27.
export default function BuyingSession(): JSX.Element {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  if (!sessionId) {
    return <StartSessionLanding />;
  }
  return <BuyingSessionContent sessionId={sessionId} key={sessionId} />;
}

function StartSessionLanding(): JSX.Element {
  const { activeDistributor } = useApp();
  const { createSession } = useBuyingSession(null);
  const { openBuyingSessionTab } = useSessionTabs();
  // Day 76 P5.1 Phase 5 — useParamStore mount for param_snapshot capture
  // at createSession. Closes Day 54 follow-up (BACKLOG topic anchor
  // "Day 54 follow-up: param_snapshot wiring"). Pattern: capture full
  // params hierarchy state (Sol option (ii) full-state lock) at the
  // moment Start Session is clicked + pass to createSession.
  const { allParams } = useParamStore();
  const [starting, setStarting] = useState<boolean>(false);
  const [startError, setStartError] = useState<string | null>(null);

  const handleStart = useCallback(async (): Promise<void> => {
    if (!activeDistributor) {
      setStartError('Pick a distributor before starting a session.');
      return;
    }
    setStarting(true);
    setStartError(null);
    try {
      const paramSnapshot = buildParamSnapshot(
        allParams,
        activeDistributor.id
      ) as unknown as Record<string, unknown>;
      const row = await createSession({
        distributorId: activeDistributor.id,
        paramSnapshot,
      });
      openBuyingSessionTab(row.id, `Buying Session — ${activeDistributor.code}`);
    } catch (e) {
      setStartError(e instanceof Error ? e.message : 'Failed to start session');
      setStarting(false);
    }
  }, [activeDistributor, createSession, openBuyingSessionTab, allParams]);

  // Day 76 P5.1 Phase 8B — Type 2 toolbar refactor per FUNCTIONAL_STANDARDS
  // Type 2 layout (title left + distributor display + primary action right
  // in toolbar Row 1; explanatory copy in scrollable body). Replaces the
  // Day 54 centered-card pattern per Day 55+ Layer 2 audit findings.
  // Distributor is display-only here — selector lives in TopBar; this page
  // launches a session FOR the active distributor.
  return (
    <div className="flex flex-col h-full -m-4" data-testid="start-session-card">
      {/* Toolbar Row 1 */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-surface-1">
        <h1 className="text-base font-semibold text-ink-primary">Start a Buying Session</h1>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-xs text-ink-muted">
            Distributor:{' '}
            <span className="font-mono text-ink-primary">
              {activeDistributor?.code ?? '— pick one above —'}
            </span>
          </span>
          <button
            type="button"
            onClick={() => {
              void handleStart();
            }}
            disabled={!activeDistributor || starting}
            className="arbi-btn-primary text-xs px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="start-session-confirm"
          >
            {starting ? 'Starting…' : 'Start Session'}
          </button>
        </div>
      </div>
      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4">
        <p className="text-xs text-ink-muted leading-relaxed max-w-2xl">
          Each session is an isolated workspace for evaluating products from one distributor.
          Filters, weights, and column layout persist per session; PO staging is shared across all
          your open sessions.
        </p>
        {startError && (
          <div className="text-xs text-error mt-3 max-w-2xl" role="alert">
            {startError}
          </div>
        )}
      </div>
    </div>
  );
}

interface BuyingSessionContentProps {
  sessionId: string;
}

function BuyingSessionContent({ sessionId }: BuyingSessionContentProps): JSX.Element {
  // Day 64.5 V-ROUI: viewer is the only V-ROUI consumer here (dist_rep
  // denied at /purchasing route). Hide Save as Draft, Multi-Add entry, and
  // multi-add modal mount. DetailPanel "Add to PO" was already gated in
  // Day 64 F49 work (handleAddToPO awaits result + error toast).
  // ParamsSidebar inputs + POTray are visible but Submit-side mutations
  // are gated everywhere, so any local-state changes never persist.
  const { activeDistributor, isAdmin, isViewer, user, buyingSessionPrefs, setBuyingSessionPrefs } =
    useApp();
  const { setSelectedProductId: setDebugProductId } = useDebugPanel();
  const { activePaths, activeMarketplaces, buyScoreWeightComponents } = useSettingsConfig();
  // Day 91a.5 P2C(i) — bswcActive = is_available=true filter per R-Lock-7;
  // threaded into buildSaveDefaultsPayload at handleSaveDefaults useCallback
  // (caller cascade per R-Lock-4 RE-LOCK Option A). Memoized for referential
  // stability across renders matching P2B ParamsSidebar bswcActive precedent.
  const bswcActive = useMemo(
    () => buyScoreWeightComponents.filter((r) => r.is_available),
    [buyScoreWeightComponents]
  );

  // Day 74 P4.2 — derive pathLabel from fpc.display_name (multi-add modal
  // title only — other surfaces iterate activePaths directly with
  // path.short_label inline). Falls back to PATH_LABEL_FALLBACK during the
  // ~60ms cache miss window before useSettingsConfig populates activePaths.
  // Day 60b *_FALLBACK pattern (BACKLOG L242).
  const pathLabel = useMemo<Record<string, string>>(() => {
    if (activePaths.length === 0) return PATH_LABEL_FALLBACK;
    return Object.fromEntries(activePaths.map((p) => [p.path_key, p.display_name]));
  }, [activePaths]);

  const navigate = useNavigate();
  // sessionLoading + sessionError surfacing is deferred to Phase 4-10 polish;
  // for V1, an invalid sessionId renders the empty grid (no products) until
  // the user navigates away. updateUiState is the only piece of the hook
  // surface BuyingSessionContent consumes today.
  const {
    session,
    loading: sessionLoading,
    error: sessionError,
    updateUiState,
    updateSafetyCap,
    saveAsDraft,
  } = useBuyingSession(sessionId);
  const gridRef = useRef<AgGridReact<GridRow> | null>(null);
  // Distributor scoping per ADR Decision 28: session-scoped, falling back to
  // global activeDistributor only if the session row hasn't loaded yet.
  const distributorId = session?.distributor_id ?? activeDistributor?.id ?? null;

  // Build TABS from active fulfillment paths config. Day 74 P4.2 retired
  // the MARKETPLACES hardcoded fallback — the *_FALLBACK constants at
  // module scope (MP_SHORT_FALLBACK / PATH_LABEL_FALLBACK) cover the cache
  // miss window via mpShort / pathLabel. Empty-activePaths case returns
  // [All Channels] only; channel tabs append once activePaths populates.
  const TABS = useMemo<PathTab[]>(() => {
    const channels: PathTab[] = activePaths.map((p) => ({
      id: p.path_key as FulfillmentPath,
      label: p.display_name,
      short: p.short_label ?? p.display_name,
    }));
    return [{ id: 'all', label: 'All Channels', short: 'All' }, ...channels];
  }, [activePaths]);

  // ── Panel state ───────────────────────────────────────────
  const [paramsPanelOpen, setParamsPanelOpen] = useState<boolean>(true);
  const [detailPanelOpen, setDetailPanelOpen] = useState<boolean>(false);
  const [poTrayOpen, setPoTrayOpen] = useState<boolean>(false);

  // ── Toolbar state ─────────────────────────────────────────
  const [selectedPath, setSelectedPath] = useState<PathSelection>('all');
  const [safetyCap, setSafetyCap] = useState<boolean>(true);
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [searchText, setSearchText] = useState<string>('');
  const [activeSort, setActiveSort] = useState<SortKey | null>(null);

  // ── Day 58 — Tier mode session config ─────────────────────
  // Persisted in ui_state.tier_mode JSONB (no sidecar). Hydrated below in the
  // ui_state hydration useEffect; written back via the same debounced patch
  // path. Single-channel only — All Channels disables the toggle.
  const [tierMode, setTierMode] = useState<BuyingSessionTierMode>({
    enabled: false,
    import_id: null,
    tiers: [],
  });
  const [algopixImports, setAlgopixImports] = useState<
    Array<{ id: string; file_name: string; imported_at: string | null }>
  >([]);
  const [tierResults, setTierResults] = useState<Record<string, TierResultByProduct> | null>(null);
  const [tierConfigOpen, setTierConfigOpen] = useState<boolean>(false);

  // ── Brand filter ──────────────────────────────────────────
  const [brandList, setBrandList] = useState<string[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null); // null = all
  const [onlyBranded, setOnlyBranded] = useState<boolean>(false);
  const [matchedBrandsOnly, setMatchedBrandsOnly] = useState<boolean>(false);

  // ── Manufacturer filter ────────────────────────────────────
  // Each entry carries the brand names assigned to the manufacturer so the
  // datasource can filter products.brand IN (names) directly — mirroring
  // the Matched Brands pattern. Only manufacturers with >= 1 brand appear
  // in the dropdown.
  const [manufacturers, setManufacturers] = useState<ManufacturerEntry[]>([]);
  const [selectedMfrIds, setSelectedMfrIds] = useState<Set<string>>(new Set());
  const [mfrDropdownOpen, setMfrDropdownOpen] = useState<boolean>(false);

  useEffect(() => {
    Promise.all([
      supabase.from('manufacturers').select('id, name').eq('is_active', true).order('name'),
      supabase.from('brands').select('id, name, manufacturer_id').order('name'),
    ]).then(([mRes, bRes]) => {
      const mRows = (mRes.data as unknown as Array<{ id: string; name: string }> | null) || [];
      const bRows =
        (bRes.data as unknown as Array<{
          id: string;
          name: string | null;
          manufacturer_id: string | null;
        }> | null) || [];
      const byMfr: Record<string, string[]> = {};
      for (const b of bRows) {
        if (!b.manufacturer_id || !b.name) continue;
        if (!byMfr[b.manufacturer_id]) byMfr[b.manufacturer_id] = [];
        byMfr[b.manufacturer_id].push(b.name);
      }
      const entries: ManufacturerEntry[] = mRows
        .map((m) => ({ id: m.id, name: m.name, brandNames: byMfr[m.id] || [] }))
        .filter((e) => e.brandNames.length > 0);
      setManufacturers(entries);
    });
  }, []);

  // Flattened brand-name list for the selected manufacturers. Empty when
  // no manufacturer is selected — which means "no filter".
  const manufacturerBrandNames = useMemo<string[]>(() => {
    if (selectedMfrIds.size === 0) return [];
    const set = new Set<string>();
    for (const m of manufacturers) {
      if (selectedMfrIds.has(m.id)) {
        for (const n of m.brandNames) set.add(n);
      }
    }
    return Array.from(set);
  }, [selectedMfrIds, manufacturers]);

  // ── Column presets ────────────────────────────────────────
  const [columnsOpen, setColumnsOpen] = useState<boolean>(false);
  // Day 54 Phase 6 — column state lives in session.ui_state. Initial empty
  // values; the hydration useEffect below populates from session.ui_state on
  // session load, and the debounced writeback useEffect persists changes back.
  // Migration bridge for users with legacy localStorage values: see
  // useBuyingSession.createSession (one-time hydration on new session create,
  // removed in Day 55).
  const [hiddenCols, setHiddenCols] = useState<string[]>([]);
  const [columnPresets, setColumnPresets] = useState<ColumnPreset[]>([]);
  const [presetName, setPresetName] = useState<string>('');

  // Toggleable columns (single-channel view)
  const TOGGLEABLE_COLS = useMemo<ToggleableCol[]>(() => {
    const base: ToggleableCol[] = [
      { id: 'brand', label: 'Brand' },
      { id: 'cost', label: 'Cost' },
      { id: 'sale_price', label: 'Sale Price' },
      { id: 'profit_per_unit', label: 'Profit/Unit' },
      { id: 'roi_pct', label: 'ROI% (landed)' }, // FN-4 P-2: landed-cost denominator (engine reorderability) — distinct from the toolbar's "ROI% (outlay)"
      { id: 'profit_per_day', label: 'Profit/Day' },
      { id: 'roi_per_day', label: 'ROI/Day' },
      { id: 'sales_per_month', label: 'Sales/Mo' },
      { id: 'days_locked', label: 'Days/Unit' },
      { id: 'effective_floor', label: 'Floor' },
      { id: 'buy_score', label: 'Buy Score' },
      { id: '_status', label: 'Status' },
    ];
    if (selectedPath === 'all') {
      // Day 74 P4.2 — per-path dot column toggles (data-driven from
      // activePaths) + per-path ROI column toggles (5-pathColKeys structure
      // matches channelRoiRenderers per A2-γ "preserve asymmetry" lock).
      for (const path of activePaths) {
        base.push({
          id: `_ch_${path.path_key}`,
          label: path.short_label ?? path.path_key,
        });
      }
      const roiKeys: ReadonlyArray<{ key: string; label: string }> = [
        { key: 'amazon_fba', label: 'FBA ROI%' },
        { key: 'amazon_fbm', label: 'FBM ROI%' },
        { key: 'ebay', label: 'eBay ROI%' },
        { key: 'wmt', label: 'WMT ROI%' },
        { key: 'shopify', label: 'Shopify ROI%' },
      ];
      for (const { key, label } of roiKeys) {
        base.push({ id: `_roi_${key}`, label });
      }
    }
    return base;
  }, [selectedPath, activePaths]);

  const toggleColumn = useCallback((colId: string) => {
    setHiddenCols((prev) =>
      prev.includes(colId) ? prev.filter((c) => c !== colId) : [...prev, colId]
    );
  }, []);

  const saveColumnPreset = useCallback(() => {
    if (!presetName.trim()) return;
    setColumnPresets((prev) => [
      ...prev.filter((p) => p.name !== presetName.trim()),
      { name: presetName.trim(), hidden: [...hiddenCols] },
    ]);
    setPresetName('');
  }, [presetName, hiddenCols]);

  const applyColumnPreset = useCallback((preset: ColumnPreset) => {
    setHiddenCols(preset.hidden);
  }, []);

  const deleteColumnPreset = useCallback((name: string) => {
    setColumnPresets((prev) => prev.filter((p) => p.name !== name));
  }, []);

  const resetColumns = useCallback(() => {
    setHiddenCols([]);
  }, []);

  useEffect(() => {
    supabase
      .from('brands')
      .select('name')
      .eq('is_active', true)
      .order('name')
      .then(({ data }) => {
        if (data) {
          const rows = data as BrandRow[];
          setBrandList(rows.map((b) => b.name).filter((n): n is string => !!n));
        }
      });
  }, []);

  // Day 58 — ingestion_batches list for the tier-mode supplier sheet selector.
  // Re-fetches when the active distributor changes. Empty array is a valid
  // state (the sheet selector renders an empty-state message in that case).
  useEffect(() => {
    if (!distributorId) {
      setAlgopixImports([]);
      return;
    }
    supabase
      .from('ingestion_batches')
      .select('id, file_name, imported_at')
      .eq('distributor_id', distributorId)
      .eq('status', 'complete')
      .order('imported_at', { ascending: false })
      .then(({ data }) => {
        const list =
          ((data ?? []) as Array<{ id: string; file_name: string; imported_at: string | null }>) ||
          [];
        setAlgopixImports(list);
        // Day 79 P5.4 F44 — orphan tier-sheet detection. If tierMode.import_id
        // references an import that no longer appears in the fetched list
        // (deleted, or status flipped away from 'complete'), surface info toast
        // and clear import_id while preserving enabled+tiers so user can re-pick.
        // Functional setTierMode avoids stale-closure on tierMode.import_id —
        // the dep array stays [distributorId] so this effect doesn't re-fire on
        // every tier toggle change.
        setTierMode((prev) => {
          if (prev.import_id && !list.some((imp) => imp.id === prev.import_id)) {
            setToast({
              kind: 'info',
              message: 'Tier sheet no longer available — pick another in the sidebar',
            });
            return { ...prev, import_id: null };
          }
          return prev;
        });
      });
  }, [distributorId]);

  // ── Session params (filters, weights, constraints) ────────
  const [filters, setFilters] = useState<Record<string, string | undefined>>({});
  const [weights, setWeights] = useState<Record<string, WeightState | undefined>>({});
  const [constraints, setConstraints] = useState<Record<string, string | undefined>>({});
  // Day 98.1b §10 — advisory concentration guardrails. Threaded exactly as
  // constraints (chip row, ui_state, save-defaults, nav-state to POBuilder).
  const [guardrails, setGuardrails] = useState<Record<string, string | undefined>>({});

  // ── Day 77 P5.2 B.4 — Chip row derivations (ADR Decision 44) ───────
  // Day 81.5 P2.F2 — compute helpers extracted to chipRow.ts; the useMemo
  // wrappers below preserve referential stability per React's memoization
  // semantics (dependency-list driven).
  const sidebarChips = useMemo<FilterChip[]>(
    () =>
      computeSidebarChips({
        filters,
        constraints,
        guardrails,
        selectedPath,
        tierModeEnabled: tierMode.enabled,
      }),
    [filters, constraints, guardrails, selectedPath, tierMode.enabled]
  );

  const manualChips = useMemo<FilterChip[]>(
    () =>
      computeManualChips({
        selectedBrand,
        selectedMfrIds,
        manufacturers,
        onlyBranded,
        matchedBrandsOnly,
      }),
    [selectedBrand, selectedMfrIds, manufacturers, onlyBranded, matchedBrandsOnly]
  );

  const allChips = useMemo<FilterChip[]>(
    () => [...sidebarChips, ...manualChips],
    [sidebarChips, manualChips]
  );

  // ── Day 54 Phase 4 — scenario picker + Save Preset modal + toast ──

  const [activeScenarioId, setActiveScenarioId] = useState<string | null>(null);

  const [savePresetOpen, setSavePresetOpen] = useState<boolean>(false);
  const [savePresetName, setSavePresetName] = useState<string>('');
  const [savePresetScope, setSavePresetScope] = useState<'current' | 'all'>('current');
  const [savePresetSubmitting, setSavePresetSubmitting] = useState<boolean>(false);
  const [savePresetError, setSavePresetError] = useState<string | null>(null);

  const [toast, setToast] = useState<ToastState | null>(null);

  // Day 80 P5.5 L528 — hydration race fix. Stays false until the
  // ui_state hydration effect runs once per session.id; loading gate
  // below blocks the main grid render until then so pre-hydration clicks
  // can't race the debounced writeback.
  const [hydrated, setHydrated] = useState<boolean>(false);

  const savePresetNameRef = useRef<HTMLInputElement | null>(null);

  const { scenarios, error: scenariosError, saveScenario } = useScenarios(distributorId);

  // Day 80 P5.5 F47 — info toast when scenarios fetch fails. Fires once
  // per error transition (null → set); picker also renders disabled with
  // "Scenarios unavailable" placeholder via ParamsSidebar scenariosError
  // prop. TONE_STANDARDS §5 Info Toasts: "Used sparingly — only for
  // information the user needs but didn't explicitly trigger."
  useEffect(() => {
    if (scenariosError) {
      setToast({ kind: 'info', message: 'Scenarios unavailable' });
    }
  }, [scenariosError]);

  // Day 73 P4.1 — stable Set instances for stale-key cleanup (C.9) +
  // drift detection (BACKLOG L427). Keys come from buyingSessionDefaults
  // (FILTER_FIELDS + CONSTRAINT_FIELDS source-of-truth) + bswc table
  // (weightKeys per Day 91a.5 P2E in-Phase migration of #DBN-Phase-2E-1
  // — Day 91a.5 enumeration-drift catch). Renames propagate from one source
  // each: FILTER_FIELDS + CONSTRAINT_FIELDS via buyingSessionDefaults.ts;
  // weight keys via buy_score_weight_components.is_available=true bswc rows
  // (admin-editable via Engine Editor → Weight Components per Day 2.5).
  const validKeys = useMemo<ValidKeySets>(
    () => ({
      filterKeys: new Set(FILTER_FIELDS.map((f) => f.key)),
      weightKeys: new Set(bswcActive.map((w) => w.key)),
      constraintKeys: new Set(CONSTRAINT_FIELDS.map((c) => c.key)),
    }),
    [bswcActive]
  );

  // Day 73 P4.1 — second useParamStore mount alongside ParamsSidebar's.
  // Two fetches today (no shared cache); acceptable until a future day
  // unifies via context provider or React Query.
  const paramStoreForDrift = useParamStore();

  // Same prefs-overridden defaults the user sees in the UI (Decision 4.4b),
  // so "typing the displayed default" doesn't count as drift.
  const paramDefaults = useMemo<Record<string, string>>(
    () => computeParamDefaults(paramStoreForDrift, distributorId, buyingSessionPrefs),
    [paramStoreForDrift, distributorId, buyingSessionPrefs]
  );

  // Auto-dismiss toast after 4s per COMPONENT_STANDARDS.md §6
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // Programmatic focus on Save Preset modal open — replaces the autoFocus prop
  // (jsx-a11y/no-autofocus). AddProductsModal.tsx precedent.
  useEffect(() => {
    if (!savePresetOpen) return;
    savePresetNameRef.current?.focus();
  }, [savePresetOpen]);

  // ── Day 54 Phase 5 — Save as Draft modal ──

  const [saveAsDraftOpen, setSaveAsDraftOpen] = useState<boolean>(false);
  const [saveAsDraftName, setSaveAsDraftName] = useState<string>('');
  const [saveAsDraftSubmitting, setSaveAsDraftSubmitting] = useState<boolean>(false);
  const [saveAsDraftError, setSaveAsDraftError] = useState<string | null>(null);

  // Programmatic focus per Phase 4 precedent (jsx-a11y/no-autofocus avoidance).
  const saveAsDraftNameRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (!saveAsDraftOpen) return;
    saveAsDraftNameRef.current?.focus();
  }, [saveAsDraftOpen]);

  // ── Session ui_state hydration + debounced writeback ──────
  // On session change (parent remounts with key={sessionId}), hydrate local
  // state from session.ui_state AND top-level columns (safety_cap_enabled).
  // Day 76 P5.1 B.1 loosened the guard from `if (!session?.ui_state) return;`
  // to `if (!session) return;` + inline `if (ui)` block so safetyCap hydrates
  // even when ui_state is NULL (existing pre-Day-54 sessions). A second
  // debounced effect writes any local state changes back to ui_state with
  // 300ms debouncing — single-effect pattern preferred over wrapping every
  // setter so consumers stay simple.
  useEffect(() => {
    if (!session) return;
    const ui = session.ui_state;
    if (ui) {
      if (ui.filters) setFilters(ui.filters);
      if (ui.weights) setWeights(ui.weights);
      if (ui.constraints) setConstraints(ui.constraints);
      if (ui.guardrails) setGuardrails(ui.guardrails);
      if (ui.hidden_cols) setHiddenCols(ui.hidden_cols);
      if (ui.column_presets) setColumnPresets(ui.column_presets);
      if (typeof ui.marketplace === 'string') setSelectedPath(ui.marketplace as PathSelection);
      if (typeof ui.filter_mode === 'string') setFilterMode(ui.filter_mode as FilterMode);
      if (typeof ui.sort_key === 'string' || ui.sort_key === null) {
        setActiveSort(ui.sort_key as SortKey | null);
      }
      if (typeof ui.search_text === 'string') setSearchText(ui.search_text);
      if (typeof ui.active_scenario_id === 'string' || ui.active_scenario_id === null) {
        setActiveScenarioId(ui.active_scenario_id ?? null);
      }
      // Day 58 — tier_mode hydration. Default to disabled-empty when absent
      // (existing sessions written before Day 58 don't carry the key).
      if (ui.tier_mode) {
        setTierMode({
          enabled: !!ui.tier_mode.enabled,
          import_id: ui.tier_mode.import_id ?? null,
          tiers: Array.isArray(ui.tier_mode.tiers) ? ui.tier_mode.tiers : [],
        });
      }
      // Day 76 P5.1 B.2 — brand + manufacturer filter persistence (Audit #1
      // F10 closure). Type-narrow guards mirror existing keys; pre-Day-76
      // sessions miss all 4 keys → guards fail → React initial state preserved
      // (selectedBrand=null, onlyBranded=false, matchedBrandsOnly=false,
      // selectedMfrIds=empty Set). Set ↔ array serialization round-trip:
      // write side uses Array.from(); hydration uses new Set().
      if (typeof ui.selected_brand === 'string' || ui.selected_brand === null) {
        setSelectedBrand(ui.selected_brand ?? null);
      }
      if (typeof ui.only_branded === 'boolean') setOnlyBranded(ui.only_branded);
      if (typeof ui.matched_brands_only === 'boolean') {
        setMatchedBrandsOnly(ui.matched_brands_only);
      }
      if (Array.isArray(ui.selected_mfr_ids)) {
        setSelectedMfrIds(new Set(ui.selected_mfr_ids));
      }
      // Day 91b F62 — panel open state hydration. Missing-key short-circuit
      // preserves React initial state (paramsPanelOpen=true; others=false).
      if (typeof ui.params_panel_open === 'boolean') setParamsPanelOpen(ui.params_panel_open);
      if (typeof ui.detail_panel_open === 'boolean') setDetailPanelOpen(ui.detail_panel_open);
      if (typeof ui.po_tray_open === 'boolean') setPoTrayOpen(ui.po_tray_open);
    }
    // Day 76 P5.1 B.1 — safetyCap from top-level column. Default TRUE matches
    // schema default + React useState<boolean>(true) initial state. Hydrates
    // unconditionally (outside the ui? guard) since safety_cap_enabled lives
    // at top-level not inside ui_state.
    setSafetyCap(session.safety_cap_enabled ?? true);
    // Day 80 P5.5 L528 — flip hydrated flag last so the loading gate below
    // releases only after every setX above has fired. Subsequent renders see
    // hydrated=true and unblock the grid + interactive surfaces.
    setHydrated(true);
    // Hydration runs once per session.id — a re-hydrate on every ui_state
    // mutation would race the debounced write. Suppressing exhaustive-deps
    // is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  // Day 76 P5.1 B.3 closure (Phase 4E) — seed local `weights` state from
  // per-user buying_session_prefs.weights[].default on session init per Sol
  // lock 2026-05-17 option (a). Without this, weight defaults written by
  // handleSaveDefaults are persisted-but-invisible in new sessions (operator
  // would experience "I clicked My Defaults but my weight values didn't
  // apply"). Precedence: ui_state.weights wins over prefs (session-local
  // override beats global default). Runs once per session.id post the
  // ui_state hydration effect above.
  useEffect(() => {
    if (!session?.id) return;
    const prefsWeights = buyingSessionPrefs?.weights;
    if (!prefsWeights?.length) return;
    const ui = session.ui_state;
    const uiWeights = ui?.weights ?? {};
    const seeded: Record<string, { enabled: boolean; weight: string } | undefined> = {
      ...uiWeights,
    };
    let mutated = false;
    for (const w of prefsWeights) {
      if (w.default == null) continue;
      if (seeded[w.key] !== undefined) continue; // ui_state value wins
      seeded[w.key] = { enabled: true, weight: String(w.default) };
      mutated = true;
    }
    if (mutated) setWeights(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  useEffect(() => {
    if (!session?.id) return;
    const patch: Partial<BuyingSessionUiState> = {
      filters,
      weights,
      constraints,
      guardrails,
      hidden_cols: hiddenCols,
      column_presets: columnPresets,
      active_scenario_id: activeScenarioId,
      // Stored ui_state JSONB key rides the legacy name until the enum dies
      // (slice-B family; zero-DDL window) — state renamed selectedPath, key not.
      marketplace: selectedPath,
      filter_mode: filterMode,
      sort_key: activeSort,
      search_text: searchText,
      tier_mode: tierMode,
      // Day 76 P5.1 B.2 — brand + manufacturer filter persistence.
      // Set → string[] via Array.from() for JSONB; hydration rehydrates
      // via new Set() (Audit #1 F10 closure).
      selected_brand: selectedBrand,
      only_branded: onlyBranded,
      matched_brands_only: matchedBrandsOnly,
      selected_mfr_ids: Array.from(selectedMfrIds),
      // Day 91b F62 — panel open state persistence. camelCase React state
      // → snake_case JSONB keys per R-Lock-10.
      params_panel_open: paramsPanelOpen,
      detail_panel_open: detailPanelOpen,
      po_tray_open: poTrayOpen,
    };
    const t = setTimeout(() => {
      void updateUiState(patch);
    }, 300);
    return () => clearTimeout(t);
  }, [
    session?.id,
    filters,
    weights,
    constraints,
    guardrails,
    hiddenCols,
    columnPresets,
    activeScenarioId,
    selectedPath,
    filterMode,
    activeSort,
    searchText,
    tierMode,
    // Day 76 P5.1 B.2
    selectedBrand,
    onlyBranded,
    matchedBrandsOnly,
    selectedMfrIds,
    // Day 91b F62 — panel open state persistence dep additions.
    paramsPanelOpen,
    detailPanelOpen,
    poTrayOpen,
    updateUiState,
  ]);

  // Day 91b F58 — backstop for runtime activeSort changes after grid is
  // ready. Sort-button onClick at L3346-area covers most runtime mutations;
  // this effect catches programmatic activeSort writes (e.g., scenario
  // apply if scenarios ever carry sort state) that don't go through the
  // onClick path. Early-return guard handles the gridApiRef-null window
  // before onGridReady has fired. Hydrated guard ensures persisted sort
  // is applied via onGridReady inline-read path (R-Lock-5) at first mount
  // rather than racing with this effect.
  useEffect(() => {
    if (!gridApiRef.current) return;
    if (!hydrated) return;
    if (activeSort) {
      gridApiRef.current.applyColumnState({
        state: [{ colId: activeSort, sort: 'desc' }],
        defaultState: { sort: null },
      });
    } else {
      gridApiRef.current.applyColumnState({ defaultState: { sort: null } });
    }
  }, [hydrated, activeSort]);

  // Day 76 P5.1 B.1 — safetyCap top-level column writeback (separate from
  // ui_state writeback above; safety_cap_enabled lives at top-level not
  // inside ui_state). Race-prevention guard: skip when session value already
  // matches React state. Without the guard, the hydration sequence breaks —
  // session loads with persisted FALSE → hydration fires setSafetyCap(false)
  // → BUT this writeback effect may fire first with stale safetyCap=true →
  // would clobber persisted FALSE → guard short-circuits the no-op write.
  // Dep on session?.safety_cap_enabled is load-bearing: when hydration
  // updates session.safety_cap_enabled via updateSafetyCap's local-state-
  // first pattern (Phase 1B), the effect re-fires with both values matching
  // and the guard short-circuits cleanly.
  useEffect(() => {
    if (!session?.id) return;
    if (session.safety_cap_enabled === safetyCap) return;
    const t = setTimeout(() => {
      void updateSafetyCap(safetyCap);
    }, 300);
    return () => clearTimeout(t);
  }, [session?.id, session?.safety_cap_enabled, safetyCap, updateSafetyCap]);

  // ── Selection & detail ────────────────────────────────────
  const [selectedProduct, setSelectedProduct] = useState<BuyingSessionProduct | null>(null);
  const [selectedChannels, setSelectedChannels] = useState<
    Record<string, ChannelProfitRow | undefined>
  >({});

  // ── PO tray (persistent via Supabase) ────────────────────
  // Day 80 P5.5 F48 — onError surfaces silent mutation failures via toast.
  const poStaging = usePOStaging({
    onError: (message) => setToast({ kind: 'error', message }),
  });

  // ── Multi-add flow ─────────────────────────────────────────
  // Selected grid rows, driven by AG Grid's onSelectionChanged. The bulk
  // action bar renders whenever this is non-empty, and the Add-to-PO click
  // captures it into multiAddDrafts + opens the modal.
  const [selectedGridRows, setSelectedGridRows] = useState<GridRow[]>([]);
  const [multiAddOpen, setMultiAddOpen] = useState<boolean>(false);
  const [multiAddDrafts, setMultiAddDrafts] = useState<MultiAddDraft[]>([]);
  // The path picked inside the modal. In single-channel mode this is
  // fixed to the active tab; in All Channels mode it starts from the first
  // active path and can be switched.
  const [multiAddPath, setMultiAddMp] = useState<string>('');
  const [multiAddSubmitting, setMultiAddSubmitting] = useState<boolean>(false);
  // All Channels mode: gate Submit on a deliberate path confirmation
  // so users don't accidentally drop the whole batch onto seedPath's default
  // (typically FBA). Single-channel mode skips this gate — the path
  // is already pinned by the active tab. Surfaced as B-R2-5 in UAT Round 2.
  const [pathConfirmed, setPathConfirmed] = useState<boolean>(false);

  // ── PO Impact: allocation-honoring engine compute via calculate_po_impact_regrounded (Day 72 P3.5; U10 re-point) ──
  // Replaces best-channel client-side compute. RPC honors po_staging.allocations
  // per Q1 (single-path fallback when allocations IS NULL). ROI% uses raw-cost
  // denominator (operator outlay-return form) — see migration COMMENT for the
  // documented divergence from engine roi_pct column (landed-cost denominator).
  // Hover tooltip on toolbar pill cluster surfaces sparse per-path breakdown.
  const [poImpact, setPoImpact] = useState<POImpactResult | null>(null);
  const [poImpactHover, setPoImpactHover] = useState<boolean>(false);
  useEffect(() => {
    if (!distributorId || poStaging.items.length === 0) {
      setPoImpact(null);
      return;
    }
    // Abort-on-unmount guard (mirrors usePoConcentration). Without it, navigating
    // away while this RPC is in flight aborts the fetch → supabase-js resolves
    // { error: 'Failed to fetch' } → the orphaned .then logs a spurious console
    // error + setPoImpact on an unmounted component. poImpact is ephemeral
    // (recomputed from DB-backed po_staging on return), so the guard only
    // silences the benign teardown noise — it changes no persisted state.
    let cancelled = false;
    supabase
      .rpc('calculate_po_impact_regrounded', {
        p_distributor_id: distributorId,
        p_safety_cap: safetyCap,
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error('[calculate_po_impact_regrounded] error:', error.message);
          setPoImpact(null);
          return;
        }
        setPoImpact(data as unknown as POImpactResult);
      });
    return () => {
      cancelled = true;
    };
  }, [distributorId, poStaging.items, safetyCap]);

  // ── Recalculate ───────────────────────────────────────────
  const [recalculating, setRecalculating] = useState<boolean>(false);
  const [calcTimestamp, setCalcTimestamp] = useState<string | null>(null);
  // DD-08: cache-staleness visibility — true when a product input changed AFTER the
  // freshest buy_score compute for this distributor (a recalc would refresh stale scores).
  const [buyScoreStale, setBuyScoreStale] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch last calc timestamp on mount
  useEffect(() => {
    if (!distributorId) return;
    supabase
      .from('product_profitability')
      .select('calculated_at')
      .eq('distributor_id', distributorId)
      .not('calculated_at', 'is', null)
      .order('calculated_at', { ascending: false })
      .limit(1)
      .then(({ data }) => {
        const rows = (data ?? []) as Array<{ calculated_at: string | null }>;
        const ts = rows[0]?.calculated_at;
        if (ts) {
          setCalcTimestamp(ts);
        }
      });
  }, [distributorId]);

  // DD-08: inputs-changed-since flag. Compare the freshest buy_score_calculated_at
  // (DD-08 column, stamped by trg_touch_buy_score_calculated_at) against the latest
  // product input change for this distributor. Re-checks when calcTimestamp moves
  // (a fresh recalc clears the flag). Silent on any fetch failure (visibility-only).
  useEffect(() => {
    if (!distributorId) {
      setBuyScoreStale(false);
      return;
    }
    void (async () => {
      const [{ data: bs }, { data: pu }] = await Promise.all([
        supabase
          .from('product_profitability')
          .select('buy_score_calculated_at')
          .eq('distributor_id', distributorId)
          .not('buy_score_calculated_at', 'is', null)
          .order('buy_score_calculated_at', { ascending: false })
          .limit(1),
        supabase
          .from('products')
          .select('updated_at')
          .eq('distributor_id', distributorId)
          .order('updated_at', { ascending: false })
          .limit(1),
      ]);
      const computed = (bs as Array<{ buy_score_calculated_at: string | null }> | null)?.[0]
        ?.buy_score_calculated_at;
      const inputChanged = (pu as Array<{ updated_at: string | null }> | null)?.[0]?.updated_at;
      setBuyScoreStale(
        Boolean(computed && inputChanged && new Date(inputChanged) > new Date(computed))
      );
    })();
  }, [distributorId, calcTimestamp]);

  // ── AG Grid datasource ────────────────────────────────────
  //
  // getRows reads current state from stateRef so the function is stable.
  // Whenever key deps change we re-set the datasource on the grid API
  // with a NEW object reference — this forces AG Grid to discard all
  // cached blocks and call getRows from scratch (purgeInfiniteCache
  // only marks blocks for lazy reload on scroll, which doesn't work).

  const gridApiRef = useRef<GridApi<GridRow> | null>(null);

  // Keep latest values in a ref so getRows always reads current state.
  // Day 91b F58 — activeSort added so onGridReady can read hydration-written
  // sort state at first mount without closure-staleness (useCallback closure
  // would capture initial-render null).
  const stateRef = useRef<StateRefShape>({
    distributorId,
    selectedPath,
    filterMode,
    searchText,
    safetyCap,
    filters,
    constraints,
    currentUserId: user?.id ?? null,
    selectedBrand,
    onlyBranded,
    matchedBrandsOnly,
    brandList,
    manufacturerBrandNames,
    tierMode,
    activeSort,
  });
  stateRef.current = {
    distributorId,
    selectedPath,
    filterMode,
    searchText,
    safetyCap,
    filters,
    constraints,
    currentUserId: user?.id ?? null,
    selectedBrand,
    onlyBranded,
    matchedBrandsOnly,
    brandList,
    manufacturerBrandNames,
    tierMode,
    activeSort,
  };

  // Stable getRows function — reads current state from stateRef
  const getRowsFn = useCallback(async (params: IGetRowsParams) => {
    const { startRow, endRow, successCallback } = params;
    const {
      distributorId: distId,
      selectedPath: path,
      filterMode: fm,
      searchText: st,
      selectedBrand: brand,
      onlyBranded: branded,
      matchedBrandsOnly: matchedOnly,
      brandList: brands,
      manufacturerBrandNames: mfrBrandNames,
    } = stateRef.current;

    if (!distId) {
      console.warn('[BuyingSession] getRows: no distributorId, returning empty');
      successCallback([], 0);
      return;
    }

    // DD-19 (U10 slice A) — the page-owned window fetch: bespoke predicates,
    // projections, and the cross-channel enrichment stay here; sort/range
    // translation and the success/fail callback contract ride the shared IRM
    // core (runIrmGetRows below).
    const fetchWindow = async ({
      startRow,
      endRow,
      sortModel,
    }: {
      startRow: number;
      endRow: number;
      sortModel: IGetRowsParams['sortModel'];
    }): Promise<{ rows: GridRow[]; total: number }> => {
      if (path === 'all') {
        // ── Cross-channel: query products, attach profitability per channel
        const f = stateRef.current.filters || {};

        // Day 100a — the three roi/margin/profit MIN filters run server-side as
        // ALIASED product_profitability!inner existence embeds on the products
        // query (each added ONLY when its threshold parses > 0), filtered BEFORE
        // .range so count:'exact' is the true filtered total. Three INDEPENDENT
        // aliased embeds preserve independent-per-metric MAX (each metric's best
        // channel, possibly different). Replaces the former page-local Math.max-
        // after-.range block. Filter-only — row mapping ignores the embed columns;
        // _channels still comes from the display-enrichment fetch below.
        const profitEmbeds = buildCrossChannelProfitEmbeds(f);

        // Day 76 P5.1 Phase 6 — map_price + brands embed (name + enforces_map)
        // added for MAP warning chip tooltip attribution per ADR 41 Shape B.
        const baseProjection =
          'id, isg_sku, title, brand, upc, asin, image_url, weight_lbs, length_in, width_in, height_in, amazon_demand_rating, amazon_monthly_sales, amazon_seller_count, amazon_listing_url, ebay_listing_url, walmart_listing_url, algopix_recommendation, algopix_data, map_price, brands ( name, enforces_map ), product_distributors ( cost_usd, is_primary )';
        const projection = profitEmbeds.length
          ? `${baseProjection}, ${profitEmbeds
              .map((e) => `${e.alias}:product_profitability!inner(${e.column})`)
              .join(', ')}`
          : baseProjection;
        let query = supabase.from('products').select(projection, { count: 'exact' });

        // K5.3 (F9) — exclude clearance lots from the buy surface. A clearance lot is already-bought
        // received inventory (re-buy = ingest a new supplier sheet → a new lot under the master),
        // never a buy candidate. Pre-.range so count:'exact' reflects it (the C+G correctness property).
        query = query.eq('is_clearance_lot', false);

        // U10 cost re-point (:317 family) — current supply cost reads the
        // is_primary junction row; products.cost_usd is the read-copy slice B
        // drops (sync_products_primary_distributor semantics are the ground).
        // Embedded filter narrows the embed to the primary row (0-or-1).
        query = query.eq('product_distributors.is_primary', true);

        // Server-side search — escape helper handles ILIKE wildcards (% _)
        // AND PostgREST .or() reserved chars (, ( ) ") per Day 73 P4.1 C.7+F56.
        if (st.trim()) {
          const term = escapeOrIlikeTerm(st.trim());
          query = query.or(`isg_sku.ilike.${term},title.ilike.${term},brand.ilike.${term}`);
        }

        // Brand filter
        if (brand) query = query.eq('brand', brand);
        if (branded) query = query.not('brand', 'is', null).neq('brand', '');
        if (matchedOnly && brands.length > 0) query = query.in('brand', brands);
        // Manufacturer filter — flattened brand-name list from Directory →
        // Manufacturers. Empty list means no filter.
        if (mfrBrandNames.length > 0) query = query.in('brand', mfrBrandNames);

        // "My products only" constraint — Day 55 Item 9C.
        if (
          stateRef.current.constraints?.my_products_only === 'true' &&
          stateRef.current.currentUserId
        ) {
          query = query.eq('assigned_buyer_user_id', stateRef.current.currentUserId);
        }

        // Sort — only real products-table fields reach ORDER BY (DD-19 core;
        // spec at module scope).
        query = applyIrmSort(query, sortModel, CROSS_CHANNEL_SORT);

        // Day 94 P8.1 Phase 3 — wire the 4 new filters server-side on the
        // products query BEFORE .range so count:'exact' reflects the filtered
        // total (the C+G correctness property: server-side, pre-pagination, no
        // page-local-count flaw). Cross-channel uses product-level columns
        // (amazon_seller_count / amazon_buy_box_price / amazon_monthly_sales); the
        // helper returns NO referencedTable for 'all', so the includeNull .or is
        // the direct-table form (no { referencedTable } option) — contrast the
        // single-channel referenced-table form applied through the products!inner
        // embed. If a future 'all' spec adds a referencedTable, this loop needs the
        // embed branch (see the single-channel loop). (Day 100a moved the
        // roi/margin/profit MIN filters server-side too — the aliased inner embeds
        // built above; All-Channels mode no longer has a page-local filter.)
        for (const pred of buildBuyingSessionPredicates(f, 'all')) {
          if (pred.includeNull) {
            query = query.or(`${pred.column}.${pred.op}.${pred.value},${pred.column}.is.null`);
          } else if (pred.op === 'gte') {
            query = query.gte(pred.column, pred.value);
          } else {
            query = query.lte(pred.column, pred.value);
          }
        }

        // Day 95 P8.2 (H.2) — min_demand_score as a server-side .in() on the
        // categorical amazon_demand_rating, applied BEFORE .range so count:'exact'
        // stays the filtered total (same correctness property as the Day-94
        // filters). UNAVAILABLE + NULL are excluded by .in() (MIN-direction,
        // decision D). Empty set (threshold > max ordinal) → sentinel → zero rows.
        const demandSet = normalizeDemandThreshold(f.min_demand_score);
        if (demandSet) {
          query = query.in(
            'amazon_demand_rating',
            demandSet.length ? demandSet : [DEMAND_NO_MATCH_SENTINEL]
          );
        }

        // Day 100a — apply each profitability embed's filters BEFORE .range:
        // .eq distributor_id scopes the embed to this distributor (matching the
        // display fetch's scope); .gte makes it an existence join on the metric.
        // Independent aliases → independent-per-metric MAX; count:'exact' on the
        // products parent stays the true distinct-product filtered total.
        for (const e of profitEmbeds) {
          query = query
            .eq(`${e.alias}.distributor_id`, distId)
            .gte(`${e.alias}.${e.column}`, e.min);
        }

        const crossBounds = irmRangeBounds(startRow, endRow);
        query = query.range(crossBounds.from, crossBounds.to);

        const { data: products, count, error: err } = await query;
        if (err) throw err;
        if (!products || products.length === 0) {
          return { rows: [], total: count || 0 };
        }
        const productRows = products as unknown as BuyingSessionProduct[];

        // Fetch profitability for these products
        const ids = productRows.map((p) => p.id);
        const { data: profRows } = await supabase
          .from('product_profitability')
          .select('*')
          .eq('distributor_id', distId)
          .in('product_id', ids);

        const profList = (profRows ?? []) as unknown as ChannelProfitRow[];
        const profMap: Record<string, Record<string, ChannelProfitRow>> = {};
        for (const row of profList) {
          const pid = row.product_id;
          const pathKey = row.marketplace;
          if (!pid || !pathKey) continue;
          if (!profMap[pid]) profMap[pid] = {};
          profMap[pid][pathKey] = row;
        }

        const rows: AllChannelsRow[] = productRows.map((p) => {
          const { product_distributors: pd, ...rest } = p;
          return {
            ...rest,
            // Current supply cost = the is_primary junction row (0-or-1 after
            // the embedded filter). No primary → null → the grid renders '—'
            // (the honest post-drop semantic; the read-copy's retain-last-known
            // edge dies with the column).
            cost_usd: pd?.[0]?.cost_usd ?? null,
            _channels: profMap[p.id] || {},
            _isAllChannels: true,
          };
        });

        // Day 100a — roi/margin/profit are filtered server-side via the aliased
        // product_profitability!inner embeds applied above (before .range), so
        // `rows` is the enriched page with no client-side filtering and `count` is
        // the products-query count:'exact' = the true filtered total. (Former
        // page-local Math.max-after-.range block removed.)
        return { rows, total: count ?? 0 };
      } else {
        // ── Single channel: query product_profitability with product join.
        // Keep `amazon_monthly_sales` in the projection — the Sales/Mo column
        // reads it from data.products regardless of the active path so
        // the raw Algopix figure is visible even on non-Amazon channels.
        let query = supabase
          .from('product_profitability')
          .select(
            `
            *,
            products!inner (
              id, isg_sku, title, brand, upc, asin,
              image_url, weight_lbs, length_in, width_in, height_in, amazon_demand_rating,
              amazon_monthly_sales, amazon_seller_count,
              amazon_listing_url, ebay_listing_url, walmart_listing_url,
              algopix_recommendation, algopix_data,
              map_price, brands ( name, enforces_map )
            )
          `,
            { count: 'exact' }
          )
          .eq('distributor_id', distId)
          .eq('marketplace', path);

        // K5.3 (F9) — single-channel mode also surfaces lots (a lot has product_profitability rows),
        // so exclude clearance lots here too, via the inner-joined products column (the products.<col>
        // referenced-table form, like the demand filter below). Pre-.range so count:'exact' reflects it.
        query = query.eq('products.is_clearance_lot', false);

        if (fm === 'profitable') query = query.eq('is_profitable', true);
        if (fm === 'meets_roi') query = query.eq('meets_min_roi', true);

        // Apply sidebar filter values from ParamsSidebar
        const f = stateRef.current.filters || {};
        const minRoi = f.min_roi_pct;
        if (minRoi && parseFloat(minRoi) > 0) query = query.gte('roi_pct', parseFloat(minRoi));
        const minMargin = f.min_margin_pct;
        if (minMargin && parseFloat(minMargin) > 0)
          query = query.gte('margin_pct', parseFloat(minMargin));
        const minProfit = f.min_profit_unit;
        if (minProfit && parseFloat(minProfit) > 0)
          query = query.gte('profit_per_unit', parseFloat(minProfit));

        // Day 94 P8.1 — wire the 4 new sidebar filters server-side via the pure
        // predicate builder (max_competition / min_price / max_price /
        // min_velocity). Existing roi/margin/profit above stay inline (out of
        // Day-94 scope); cross-channel ('all') wiring lands Phase 3.
        for (const pred of buildBuyingSessionPredicates(f, 'single')) {
          if (pred.includeNull) {
            // MAX filter (decision D): keep rows at-or-below the cap OR with a
            // NULL value. referencedTable routes products-side columns through
            // the !inner embed (mirrors the search .or below).
            const orExpr = `${pred.column}.${pred.op}.${pred.value},${pred.column}.is.null`;
            query = pred.referencedTable
              ? query.or(orExpr, { referencedTable: pred.referencedTable })
              : query.or(orExpr);
          } else {
            // MIN filter: plain comparison (NULL excluded by Postgres .gte).
            const col = pred.referencedTable
              ? `${pred.referencedTable}.${pred.column}`
              : pred.column;
            query = pred.op === 'gte' ? query.gte(col, pred.value) : query.lte(col, pred.value);
          }
        }

        // Day 95 P8.2 (H.2) — single-channel: same filter on the inner-joined
        // products table (referenced-table prefix form, like the brand filter).
        // Before .range so count:'exact' is the filtered total.
        const demandSet = normalizeDemandThreshold(f.min_demand_score);
        if (demandSet) {
          query = query.in(
            'products.amazon_demand_rating',
            demandSet.length ? demandSet : [DEMAND_NO_MATCH_SENTINEL]
          );
        }

        // Server-side search on joined products table — escape helper handles
        // ILIKE wildcards (% _) AND PostgREST .or() reserved chars (, ( ) ")
        // per Day 73 P4.1 C.7+F56. referencedTable option unchanged.
        if (st.trim()) {
          const term = escapeOrIlikeTerm(st.trim());
          query = query.or(`isg_sku.ilike.${term},title.ilike.${term},brand.ilike.${term}`, {
            referencedTable: 'products',
          });
        }

        // Brand filter (on joined products table — use referencedTable for inner join filtering)
        if (brand) query = query.eq('products.brand', brand);
        if (branded) query = query.not('products.brand', 'is', null).neq('products.brand', '');
        if (matchedOnly && brands.length > 0) query = query.in('products.brand', brands);
        // Manufacturer filter — same semantic as above; PostgREST referenced-
        // table syntax so the IN clause lands on the joined products side.
        if (mfrBrandNames.length > 0) query = query.in('products.brand', mfrBrandNames);

        // "My products only" constraint — Day 55 Item 9C. Referenced-table
        // syntax routes the predicate to the inner-joined products side.
        if (
          stateRef.current.constraints?.my_products_only === 'true' &&
          stateRef.current.currentUserId
        ) {
          query = query.eq('products.assigned_buyer_user_id', stateRef.current.currentUserId);
        }

        // Day 58 — tier mode import_id filter. Restricts the row set to
        // products from the buyer's selected supplier sheet so tier columns
        // are scoped to the negotiation in flight. Same referenced-table
        // syntax as the brand/buyer filters above.
        const tm = stateRef.current.tierMode;
        if (tm?.enabled && tm.import_id) {
          query = query.eq('products.import_id', tm.import_id);
        }

        // Sort — pp fields only; synthetic colIds remap by the safety-cap
        // toggle (DD-19 core applies remap → allowlist → fallback).
        query = applyIrmSort(query, sortModel, {
          allowed: SINGLE_CHANNEL_SORT_COLUMNS,
          remap: (colId) => remapSafetyCapColId(colId, stateRef.current.safetyCap),
          nullsFirst: false,
          fallback: { column: 'roi_pct', ascending: false, nullsFirst: false },
        });

        const singleBounds = irmRangeBounds(startRow, endRow);
        query = query.range(singleBounds.from, singleBounds.to);

        const { data, count, error: err } = await query;
        if (err) throw err;

        return { rows: (data ?? []) as unknown as SingleChannelRow[], total: count || 0 };
      }
    };

    await runIrmGetRows<GridRow>({
      params,
      page: 'Buying Session',
      // path comes from stateRef (fresh); the old inline catch read the
      // component-state `path` through this callback's empty-deps
      // closure — always its first-render value — so the failure label now
      // reflects the actually-active grid mode.
      failureContext: {
        rpcName: path === 'all' ? 'all-channels query' : 'product_profitability',
        params: { startRow, endRow },
      },
      fetch: fetchWindow,
    });
    // reads current state via stateRef; intentional empty deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Called once when AG Grid is initialized
  const onGridReady = useCallback(
    (params: GridReadyEvent<GridRow>) => {
      gridApiRef.current = params.api;
      // Only set datasource if we have a distributorId; the effect below handles the case
      // where distributorId arrives after grid init
      if (stateRef.current.distributorId) {
        params.api.setGridOption('datasource', { getRows: getRowsFn });
      }
      // Day 91b F58 — apply persisted sort state on grid mount. Reads from
      // stateRef so hydration-written activeSort is observed (closure over
      // initial-render activeSort would be stale-null). Sort-button onClick
      // path handles runtime mutations; this block covers
      // first-mount-with-persisted-sort. Mirrors L3346-3354 applyColumnState
      // shape verbatim per R-Lock-5.
      if (stateRef.current.activeSort) {
        params.api.applyColumnState({
          state: [{ colId: stateRef.current.activeSort, sort: 'desc' }],
          defaultState: { sort: null },
        });
      }
    },
    [getRowsFn]
  );

  // Re-set the datasource whenever key deps change — a NEW object reference
  // forces AG Grid to discard all cached blocks and fetch from scratch
  useEffect(() => {
    const api = gridApiRef.current;
    if (!api || !distributorId) return;
    api.setGridOption('datasource', { getRows: getRowsFn });
  }, [
    distributorId,
    selectedPath,
    filterMode,
    searchText,
    // Day 73 P4.1 C.8 — re-sort when safetyCap toggles. Engine cache holds
    // both _safety and _velocity variants (Day 68); refetch reorders by
    // the correct column via serverColId mapping at L1153-1167. No
    // calculate_profitability call.
    safetyCap,
    filters,
    selectedBrand,
    onlyBranded,
    matchedBrandsOnly,
    manufacturerBrandNames,
    tierMode.enabled,
    tierMode.import_id,
    getRowsFn,
  ]);

  // ── Recalculate ─────────────────────────────────────────────
  //
  // Day 68 P3.1 — calculate_profitability consumes bswc.key directly.
  // Frontend sends `weights` state keys verbatim (Q1 Path B); engine owns
  // stem-to-DB-column dispatch via bswc.applies_to_metric and safety/velocity
  // suffix selection via the p_safety_cap parameter.

  const recalculate = useCallback(async () => {
    if (!distributorId || recalculating) return;
    setRecalculating(true);
    setError(null);
    try {
      // Build weights JSONB for RPC. Engine LEFT JOINs buy_score_weight_components
      // on bswc.key = jsonb key (Q1 Path B); zero frontend translation needed.
      const activeWeights: Record<string, number> = {};
      Object.entries(weights).forEach(([key, cfg]) => {
        if (!cfg) return;
        if (cfg.enabled && parseFloat(cfg.weight) > 0) {
          activeWeights[key] = parseFloat(cfg.weight) / 100;
        }
      });
      const weightsParam = Object.keys(activeWeights).length > 0 ? activeWeights : null;

      const { data, error: err } = await supabase.rpc('calculate_profitability_regrounded', {
        p_distributor_id: distributorId,
        p_weights: weightsParam,
        p_safety_cap: safetyCap,
      });
      if (err) throw err;
      // RPC returns JSONB { rows_upserted, rows_deactivated_cleanup,
      // distributor_id, calculated_at }
      const result = data as RecalculateResult | string | null;
      const ts =
        (typeof result === 'object' && result?.calculated_at) ||
        (typeof result === 'string' ? result : null) ||
        new Date().toISOString();
      setCalcTimestamp(ts);
      triggerTicker('Recalculation complete');
      // Refresh grid — re-set datasource with new object to force full re-fetch
      if (gridApiRef.current) {
        gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRecalculating(false);
    }
  }, [distributorId, recalculating, getRowsFn, weights, safetyCap]);

  // ── Column definitions ────────────────────────────────────

  // ── Cell renderer components (must return JSX for AG Grid React v35) ──────

  const TitleCellAllChannels = useCallback(
    (p: ICellRendererParams<GridRow>): JSX.Element | null => {
      const data = p.data;
      if (!data || !isAllChannelsRow(data)) return null;
      return (
        <div
          style={{
            fontWeight: 500,
            color: 'var(--ink-bright)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {data.title || '—'}
        </div>
      );
    },
    []
  );

  const TitleCellSingleChannel = useCallback(
    (p: ICellRendererParams<GridRow>): JSX.Element | null => {
      const data = p.data;
      if (!data || isAllChannelsRow(data)) return null;
      const prod = data.products;
      if (!prod) return null;
      return (
        <div
          style={{
            fontWeight: 500,
            color: 'var(--ink-bright)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {prod.title || '—'}
        </div>
      );
    },
    []
  );

  // Factory for channel dot renderers — returns a stable component per path
  type ChannelCellRenderer = (p: ICellRendererParams<GridRow>) => JSX.Element | null;
  const channelDotRenderers = useMemo<Record<string, ChannelCellRenderer>>(() => {
    const renderers: Record<string, ChannelCellRenderer> = {};
    // Day 74 P4.2 — iterate activePaths (data-driven) instead of hardcoded
    // MP_SHORT keys. Dots are 1:1 with paths; adding/removing a path via
    // fpc.is_active flow propagates through here automatically.
    for (const path of activePaths) {
      const pathKey = path.path_key;
      renderers[pathKey] = (p: ICellRendererParams<GridRow>) => {
        const data = p.data;
        if (!data || !isAllChannelsRow(data)) return null;
        const row = data._channels[pathKey];
        let bg = 'var(--surface-5)';
        let title = 'No price data';
        if (row && !row.no_price_data) {
          if (row.meets_min_roi) {
            bg = 'var(--success)';
            title = `ROI ${fmtPct(row.roi_pct)}`;
          } else if (row.is_profitable) {
            bg = 'var(--warning)';
            title = `ROI ${fmtPct(row.roi_pct)}`;
          } else {
            bg = 'var(--danger)';
            title = `ROI ${fmtPct(row.roi_pct)}`;
          }
        }
        return (
          <span
            title={title}
            style={{
              display: 'inline-block',
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: bg,
            }}
          />
        );
      };
    }
    return renderers;
  }, [activePaths]);

  const StatusCellAllChannels = useCallback(
    (p: ICellRendererParams<GridRow>): JSX.Element | null => {
      const data = p.data;
      if (!data || !isAllChannelsRow(data)) return null;
      const channelRows = Object.values(data._channels);
      const best =
        channelRows.find((c) => c.meets_min_roi) ||
        channelRows.find((c) => c.is_profitable) ||
        channelRows[0];
      if (!best) return null;
      if (best.meets_min_roi)
        return (
          <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-success/15 text-success">
            Meets ROI
          </span>
        );
      if (best.is_profitable)
        return (
          <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-warning/15 text-warning">
            Profitable
          </span>
        );
      return (
        <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-danger/15 text-danger">
          No profit
        </span>
      );
    },
    []
  );

  const StatusCellSingleChannel = useCallback(
    (p: ICellRendererParams<GridRow>): JSX.Element | null => {
      const data = p.data;
      if (!data || isAllChannelsRow(data)) return null;
      if (data.no_price_data)
        return (
          <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-surface-4 text-ink-muted">
            No price
          </span>
        );
      if (data.meets_min_roi)
        return (
          <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-success/15 text-success">
            Meets ROI
          </span>
        );
      if (data.is_profitable)
        return (
          <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-warning/15 text-warning">
            Profitable
          </span>
        );
      return (
        <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-danger/15 text-danger">
          No profit
        </span>
      );
    },
    []
  );

  // Per-channel ROI% renderers for All Channels view
  const channelRoiRenderers = useMemo<Record<string, ChannelCellRenderer>>(() => {
    const renderers: Record<string, ChannelCellRenderer> = {};
    // Outer 5-key ROI col structure preserved per A2-γ "preserve asymmetry"
    // lock (operator-facing UX choice — FBA/FBM distinct, Walmart combined).
    // Inner walmart_self/walmart_wfs literals replaced with marketplace_id
    // FK-derived path discovery per Day 74 P4.2 plan.
    const pathColKeys = [
      { key: 'amazon_fba', label: 'FBA' },
      { key: 'amazon_fbm', label: 'FBM' },
      { key: 'ebay', label: 'eBay' },
      { key: 'wmt', label: 'WMT' }, // combined virtual key (Walmart channel group)
      { key: 'shopify', label: 'Shopify' },
    ] as const;

    // Day 74 P4.2 — FK-derived Walmart paths discovery (replaces hardcoded
    // walmart_self/walmart_wfs literals). Generalizes when/if additional
    // Walmart paths are activated (e.g. a 3rd fulfillment route).
    const walmartMarketplaceId =
      activeMarketplaces.find((m) => m.channel === 'walmart')?.id ?? null;
    const walmartPathKeys = walmartMarketplaceId
      ? activePaths.filter((p) => p.marketplace_id === walmartMarketplaceId).map((p) => p.path_key)
      : [];

    for (const { key } of pathColKeys) {
      renderers[key] = (p: ICellRendererParams<GridRow>) => {
        const data = p.data;
        if (!data || !isAllChannelsRow(data))
          return <span style={{ color: 'var(--ink-muted)' }}>—</span>;
        let row: ChannelProfitRow | undefined;
        if (key === 'wmt') {
          // Best (by roi_pct) across all paths in the Walmart channel group.
          const candidates: ChannelProfitRow[] = walmartPathKeys
            .map((pk) => data._channels[pk])
            .filter((c): c is ChannelProfitRow => Boolean(c));
          if (candidates.length === 0) {
            row = undefined;
          } else if (candidates.length === 1) {
            row = candidates[0];
          } else {
            row = candidates.reduce((best, current) =>
              (current.roi_pct ?? 0) >= (best.roi_pct ?? 0) ? current : best
            );
          }
        } else {
          row = data._channels[key];
        }
        if (!row || row.no_price_data) return <span style={{ color: 'var(--ink-muted)' }}>—</span>;
        const val = row.roi_pct;
        if (val === null || val === undefined)
          return <span style={{ color: 'var(--ink-muted)' }}>—</span>;
        const color = Number(val) > 0 ? 'var(--success)' : 'var(--danger)';
        return (
          <span style={{ fontFamily: "'JetBrains Mono', monospace", color }}>{fmtPct(val)}</span>
        );
      };
    }
    return renderers;
  }, [activePaths, activeMarketplaces]);

  // Day 58 — tier RPC fire. Triggers when tier mode is fully configured AND a
  // single channel is selected. Result map keyed by product_id; consumed by
  // the tier ColDefs (T{N} ROI% + T{N} PPU). On error, surface via
  // reportDatasourceFailure (mirrors the datasource error path) and clear
  // results so the cells render as '—' rather than carrying stale data.
  useEffect(() => {
    if (
      !distributorId ||
      selectedPath === 'all' ||
      !tierMode.enabled ||
      !tierMode.import_id ||
      tierMode.tiers.length === 0 ||
      tierMode.tiers.some((t) => !(t.unit_cost > 0))
    ) {
      setTierResults(null);
      return;
    }
    let cancelled = false;
    type RpcResult = {
      products?: Array<{
        product_id: string;
        sale_price: number | null;
        map_price: number | null;
        below_map: boolean;
        by_tier: TierResultByProduct['by_tier'];
      }>;
    };
    // Narrow path away from 'all' — we already short-circuited that
    // case above. The successor keys on p_path_key (fulfillment_path_config
    // vocabulary — the U10 re-point); PathSelection already carries path
    // keys, and the RPC raises on an unknown path.
    const path = selectedPath as Exclude<PathSelection, 'all'>;
    void supabase
      .rpc('calculate_profitability_tiers_regrounded', {
        p_distributor_id: distributorId,
        p_import_id: tierMode.import_id,
        p_path_key: path,
        p_tiers: tierMode.tiers as unknown as Json,
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          reportDatasourceFailure(
            'Buying Session — Tier RPC',
            {
              rpcName: 'calculate_profitability_tiers_regrounded',
              params: { import_id: tierMode.import_id },
            },
            error,
            () => undefined
          );
          setTierResults(null);
          // Day 80 P5.5 F50 — user-visible error toast. reportDatasourceFailure
          // stays as the observability sink; toast is the operator-facing
          // surface so a silent grid-clear doesn't mask the failure.
          setToast({ kind: 'error', message: 'Tier calculation failed — try again.' });
          return;
        }
        const result = data as RpcResult | null;
        const map: Record<string, TierResultByProduct> = {};
        for (const p of result?.products ?? []) {
          // Day 69 — carry product-level fields forward (dormant until Day 76
          // P5.1 MAP UI surface).
          map[p.product_id] = {
            sale_price: p.sale_price,
            map_price: p.map_price,
            below_map: p.below_map,
            by_tier: p.by_tier,
          };
        }
        setTierResults(map);
        gridApiRef.current?.refreshInfiniteCache();
      });
    return () => {
      cancelled = true;
    };
  }, [distributorId, selectedPath, tierMode.enabled, tierMode.import_id, tierMode.tiers]);

  // Day 58 — best-tier predicate. Walks the by_tier array for a product and
  // returns true when idx === argmax(roi_pct). Memoized via tierResults
  // closure; cells call it from cellClassRules.
  const isBestTier = useCallback(
    (productId: string | undefined, tierIndex: number): boolean => {
      if (!productId) return false;
      const arr = tierResults?.[productId]?.by_tier;
      if (!arr || arr.length === 0) return false;
      let bestIdx = 0;
      let bestRoi = arr[0]?.roi_pct ?? -Infinity;
      for (let i = 1; i < arr.length; i++) {
        const r = arr[i]?.roi_pct;
        if (r != null && r > bestRoi) {
          bestRoi = r;
          bestIdx = i;
        }
      }
      return bestIdx === tierIndex;
    },
    [tierResults]
  );

  // Day 58 — dynamic tier ColDefs. 2 columns per tier (ROI% + PPU). Empty
  // when tier mode disabled. Best-tier highlight on both cells of the
  // best-ROI tier per row.
  const tierColDefs = useMemo<ColDef<GridRow>[]>(() => {
    if (!tierMode.enabled || selectedPath === 'all' || tierMode.tiers.length === 0) return [];
    const cols: ColDef<GridRow>[] = [];
    tierMode.tiers.forEach((tier, idx) => {
      const tooltip = `Tier ${idx + 1}: ${tier.qty_min}–${tier.qty_max ?? '+'} units @ $${tier.unit_cost.toFixed(2)}`;
      const cellStyle: CellStyle = {
        textAlign: 'right',
        fontFamily: "'JetBrains Mono', monospace",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      };
      cols.push({
        colId: `tier_${idx}_roi`,
        headerName: `T${idx + 1} ROI%`,
        headerTooltip: tooltip,
        width: 88,
        valueGetter: (p: ValueGetterParams<GridRow>) => {
          const data = p.data;
          if (!data || isAllChannelsRow(data)) return null;
          const productId = data.products?.id;
          return productId ? (tierResults?.[productId]?.by_tier?.[idx]?.roi_pct ?? null) : null;
        },
        valueFormatter: (p: ValueFormatterParams<GridRow>) => {
          const v = p.value as number | null;
          return v == null ? '—' : `${v.toFixed(1)}%`;
        },
        cellClassRules: {
          'bg-success/15 text-success font-semibold': (p) => {
            const data = p.data;
            if (!data || isAllChannelsRow(data)) return false;
            const productId = data.products?.id;
            return !!productId && isBestTier(productId, idx);
          },
        },
        cellStyle,
      });
      cols.push({
        colId: `tier_${idx}_ppu`,
        headerName: `T${idx + 1} PPU`,
        headerTooltip: tooltip,
        width: 88,
        valueGetter: (p: ValueGetterParams<GridRow>) => {
          const data = p.data;
          if (!data || isAllChannelsRow(data)) return null;
          const productId = data.products?.id;
          return productId ? (tierResults?.[productId]?.by_tier?.[idx]?.ppu ?? null) : null;
        },
        valueFormatter: (p: ValueFormatterParams<GridRow>) => fmt$(p.value as number | null),
        cellClassRules: {
          'bg-success/15 text-success font-semibold': (p) => {
            const data = p.data;
            if (!data || isAllChannelsRow(data)) return false;
            const productId = data.products?.id;
            return !!productId && isBestTier(productId, idx);
          },
        },
        cellStyle,
      });
    });
    return cols;
  }, [tierMode.enabled, tierMode.tiers, selectedPath, tierResults, isBestTier]);

  const columnDefs = useMemo<ColDef<GridRow>[]>(() => {
    const flexCenterStyle = { display: 'flex', alignItems: 'center' } as CellStyle;
    const flexCenterCenterStyle = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    } as CellStyle;
    const flexRightCenterStyle = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
    } as CellStyle;
    const monoStyle = { fontFamily: 'monospace' } as CellStyle;
    const monoMutedStyle = { fontFamily: 'monospace', color: 'var(--ink-muted)' } as CellStyle;
    const monoSecondaryStyle = {
      fontFamily: 'monospace',
      color: 'var(--ink-secondary)',
    } as CellStyle;
    const buyScoreStyle = {
      fontFamily: "'JetBrains Mono', monospace",
      color: 'var(--accent)',
      textAlign: 'right',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
    } as CellStyle;

    if (selectedPath === 'all') {
      return [
        {
          headerName: 'ISG SKU',
          field: 'isg_sku',
          width: 110,
          pinned: 'left',
          suppressMovable: true,
          cellRenderer: SkuCellAllChannels,
          cellStyle: flexCenterStyle,
        },
        {
          headerName: 'Title',
          field: 'title',
          flex: 1,
          minWidth: 180,
          cellRenderer: TitleCellAllChannels,
        },
        {
          headerName: 'Cost',
          field: 'cost_usd',
          // U10 cost re-point — junction-embedded cost isn't server-orderable
          // (to-many embed; cost_usd left the DD-19 allowlist with the
          // read-copy), so all-mode header sort is off, not silently a no-op.
          sortable: false,
          width: 80,
          valueFormatter: (p: ValueFormatterParams<GridRow>) => fmt$(p.value as number | null),
          cellStyle: monoStyle,
        },
        {
          headerName: 'Sales/Mo',
          colId: 'sales_per_month',
          width: 90,
          type: 'numericColumn',
          headerClass: 'ag-right-aligned-header',
          // Day 91a.5 P2D — Q11 R4 cat 2 4-step fallback chain per R-Lock-2.
          // Admin override via Engine Editor → Weight Components → tooltip_text
          // column populates bswc.tooltip_text; pre-existing hardcoded text
          // preserved as 3rd fallback step (graceful degradation while admin
          // curation pending).
          headerTooltip: resolveTooltip(
            bswcActive,
            'sales_velocity',
            'Amazon US estimated unit sales per month — raw Algopix figure. "—" means no velocity data; the engine falls back to the 30-day default.'
          ),
          valueGetter: (p: ValueGetterParams<GridRow>) => {
            const data = p.data;
            if (!data) return null;
            if (isAllChannelsRow(data)) return data.amazon_monthly_sales ?? null;
            return data.products?.amazon_monthly_sales ?? null;
          },
          valueFormatter: (p: ValueFormatterParams<GridRow>) => fmtInt(p.value as number | null),
          cellStyle: monoSecondaryStyle,
        },
        // Channel indicator dot columns — Day 74 P4.2 data-driven from
        // activePaths. Adaptive width per A5-γ lock: floor 44px (3-char
        // labels fit), ceiling 80px (max uppercase header + letter-spacing
        // budget), ~8px/char + 16px padding for intermediate sizes.
        // Accommodates 'WMT-SELF' (8 chars uppercase → ~80px) and
        // 'SHOPIFY' (7 chars → ~72px) without truncation. 'hide' clause
        // wires per-path columns into the TOGGLEABLE_COLS menu (Phase 9).
        ...activePaths.map((path): ColDef<GridRow> => {
          const label = path.short_label ?? path.path_key;
          const computedWidth = Math.max(44, Math.min(80, label.length * 8 + 16));
          return {
            headerName: label,
            colId: `_ch_${path.path_key}`,
            width: computedWidth,
            sortable: false,
            filter: false,
            hide: hiddenCols.includes(`_ch_${path.path_key}`),
            headerClass: 'ag-center-aligned-header',
            cellStyle: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: '0',
            } as CellStyle,
            cellRenderer: channelDotRenderers[path.path_key],
          };
        }),
        // Per-channel ROI% columns (All Channels view) — A2-γ lock:
        // outer 5-pathColKeys structure preserved (operator-facing UX choice:
        // FBA/FBM distinct, Walmart combined). The 'wmt' virtual key
        // resolves via marketplace_id FK inside channelRoiRenderers.
        ...(
          [
            { key: 'amazon_fba', label: 'FBA' },
            { key: 'amazon_fbm', label: 'FBM' },
            { key: 'ebay', label: 'eBay' },
            { key: 'wmt', label: 'WMT' },
            { key: 'shopify', label: 'SHPFY' },
          ] as const
        ).map(
          ({ key, label }): ColDef<GridRow> => ({
            headerName: label,
            colId: `_roi_${key}`,
            width: 65,
            sortable: false,
            filter: false,
            hide: hiddenCols.includes(`_roi_${key}`),
            headerClass: 'ag-right-aligned-header',
            cellStyle: flexRightCenterStyle,
            cellRenderer: channelRoiRenderers[key],
          })
        ),
        {
          headerName: 'Buy Score',
          colId: 'buy_score',
          width: 85,
          sortable: true,
          filter: false,
          // Day 91a.5 P2D — hardcoded composite explainer per build prompt §7
          // (rollup column; not a bswc-key consumer per Q11 R4 + R-Lock-2 scope
          // — composite of weighted components, not a single metric).
          headerTooltip:
            'Weighted composite of all enabled component metrics. See Engine Editor → Weight Components to configure components.',
          valueGetter: (p: ValueGetterParams<GridRow>) => {
            const data = p.data;
            if (!data || !isAllChannelsRow(data)) return null;
            // Day 74 P4.2 F35 — iterate activePaths in sort_order
            // (useSettingsConfig orders by sort_order); return buy_score
            // of first path with channel data. Preserves existence-order
            // semantics per A2-γ lock — sort_order match verified
            // IDENTICAL to prior hardcoded || chain (Phase 0 disk
            // verification).
            const ch = data._channels;
            for (const path of activePaths) {
              const candidate = ch?.[path.path_key];
              if (candidate) return candidate.buy_score ?? null;
            }
            return null;
          },
          valueFormatter: (p: ValueFormatterParams<GridRow>) =>
            p.value != null ? Number(p.value).toFixed(1) : '\u2014',
          cellStyle: buyScoreStyle,
          headerClass: 'ag-right-aligned-header',
        },
        {
          headerName: 'Status',
          colId: '_status',
          width: 90,
          sortable: false,
          filter: false,
          headerClass: 'ag-center-aligned-header',
          cellStyle: flexCenterCenterStyle,
          cellRenderer: StatusCellAllChannels,
        },
      ];
    }

    // Single channel columns
    return [
      {
        headerName: 'ISG SKU',
        valueGetter: (p: ValueGetterParams<GridRow>) => {
          const data = p.data;
          if (!data || isAllChannelsRow(data)) return null;
          return data.products?.isg_sku ?? null;
        },
        width: 110,
        pinned: 'left',
        suppressMovable: true,
        cellRenderer: SkuCellSingleChannel,
        cellStyle: flexCenterStyle,
      },
      {
        headerName: 'Title',
        flex: 2,
        minWidth: 200,
        cellRenderer: TitleCellSingleChannel,
      },
      {
        headerName: 'Brand',
        colId: 'brand',
        valueGetter: (p: ValueGetterParams<GridRow>) => {
          const data = p.data;
          if (!data || isAllChannelsRow(data)) return null;
          return data.products?.brand ?? null;
        },
        width: 110,
        hide: hiddenCols.includes('brand'),
        cellStyle: { color: 'var(--ink-secondary)' } as CellStyle,
      },
      {
        headerName: 'Cost',
        colId: 'cost',
        field: 'total_cost_per_unit',
        width: 80,
        type: 'numericColumn',
        hide: hiddenCols.includes('cost'),
        valueFormatter: (p: ValueFormatterParams<GridRow>) => fmt$(p.value as number | null),
        cellStyle: monoStyle,
      },
      {
        headerName: 'Sale Price',
        colId: 'sale_price',
        field: 'sale_price',
        width: 90,
        hide: hiddenCols.includes('sale_price'),
        type: 'numericColumn',
        valueFormatter: (p: ValueFormatterParams<GridRow>) => fmt$(p.value as number | null),
        cellStyle: { fontFamily: 'monospace', fontWeight: 500 } as CellStyle,
        // Day 76 P5.1 Phase 6 — MAP warning chip per ADR 41 Shape B.
        // 3-state gate: only renders chip when below_map === true.
        // Cross-channel (AllChannelsRow) has no row-level sale_price/
        // below_map; falls through to formatted string.
        // Null value renders '—' to match audit harness em-dash rule +
        // grid-wide empty-cell convention (preserved from original
        // valueFormatter-only behavior pre-Phase-6D).
        cellRenderer: (p: ICellRendererParams<GridRow>) => {
          const value = p.value as number | null | undefined;
          const formatted = fmt$(value);
          const data = p.data;
          if (value == null) return formatted;
          if (!data || isAllChannelsRow(data)) return formatted;
          // Day 84.8 P3 — wrap with ProvenanceTooltip when sale_price_source
          // present (engine attribution per F9; synthetic envelope per IA-7
          // engine-flag rendering pattern established at Phase 2 Restock).
          // MapWarningChip preserved per Day 76 P5.1 ADR 41 Shape B.
          const baseContent =
            data.below_map === true ? (
              <span className="inline-flex items-center gap-1">
                <span>{formatted}</span>
                <MapWarningChip
                  mapPrice={data.products?.map_price ?? null}
                  brandName={data.products?.brands?.name ?? null}
                />
              </span>
            ) : (
              formatted
            );
          const salePriceSource = data.sale_price_source;
          if (!salePriceSource) return baseContent;
          const syntheticProv: ProvenanceMap = {
            sale_price: { source: salePriceSource },
          };
          const staleDaysRaw = Number(
            paramStoreForDrift.resolveKey('manual_override_stale_days')?.param.value
          );
          const staleThresholdDays = Number.isFinite(staleDaysRaw) ? staleDaysRaw : 90;
          return (
            <ProvenanceTooltip
              provenance={syntheticProv}
              fieldKey="sale_price"
              staleThresholdDays={staleThresholdDays}
              mode="cell"
            >
              {baseContent}
            </ProvenanceTooltip>
          );
        },
      },
      {
        headerName: 'Profit/Unit',
        field: 'profit_per_unit',
        width: 95,
        type: 'numericColumn',
        hide: hiddenCols.includes('profit_per_unit'),
        // Day 91a.5 P2D — Q11 R4 cat 3 3-step fallback chain per R-Lock-2.
        headerTooltip: resolveTooltip(bswcActive, 'ppu'),
        valueFormatter: (p: ValueFormatterParams<GridRow>) => fmt$(p.value as number | null),
        cellStyle: (p) => {
          const v = p.value;
          if (v === null || v === undefined) return { fontFamily: 'monospace' } as CellStyle;
          const data = p.data;
          const meetsRoi = data && !isAllChannelsRow(data) ? data.meets_min_roi : false;
          const color =
            Number(v) > 0 ? (meetsRoi ? 'var(--success)' : 'var(--warning)') : 'var(--danger)';
          return { fontFamily: 'monospace', color } as CellStyle;
        },
      },
      {
        headerName: 'ROI %',
        field: 'roi_pct',
        width: 75,
        type: 'numericColumn',
        hide: hiddenCols.includes('roi_pct'),
        // Day 91a.5 P2D — Q11 R4 cat 3 3-step fallback chain per R-Lock-2.
        headerTooltip: resolveTooltip(bswcActive, 'roi_pct'),
        valueFormatter: (p: ValueFormatterParams<GridRow>) => fmtPct(p.value as number | null),
        cellStyle: (p) => {
          const v = p.value;
          if (v === null || v === undefined) return { fontFamily: 'monospace' } as CellStyle;
          const data = p.data;
          const meetsRoi = data && !isAllChannelsRow(data) ? data.meets_min_roi : false;
          const color =
            Number(v) > 0 ? (meetsRoi ? 'var(--success)' : 'var(--warning)') : 'var(--danger)';
          return { fontFamily: 'monospace', color } as CellStyle;
        },
      },
      {
        headerName: 'Profit/Day',
        colId: 'profit_per_day',
        width: 95,
        type: 'numericColumn',
        hide: hiddenCols.includes('profit_per_day'),
        // Day 91a.5 P2D — Q11 R4 cat 3 3-step fallback chain per R-Lock-2.
        headerTooltip: resolveTooltip(bswcActive, 'profit_per_day'),
        valueGetter: (p: ValueGetterParams<GridRow>) => {
          const data = p.data;
          if (!data || isAllChannelsRow(data)) return null;
          return safetyCap ? data.profit_per_day_safety : data.profit_per_day_velocity;
        },
        valueFormatter: (p: ValueFormatterParams<GridRow>) => fmt$(p.value as number | null),
        cellStyle: (p) => {
          const v = p.value;
          if (v === null || v === undefined) return { fontFamily: 'monospace' } as CellStyle;
          return {
            fontFamily: 'monospace',
            color: Number(v) > 0 ? 'var(--success)' : 'var(--danger)',
          } as CellStyle;
        },
      },
      {
        headerName: 'ROI/Day',
        colId: 'roi_per_day',
        width: 80,
        type: 'numericColumn',
        hide: hiddenCols.includes('roi_per_day'),
        // Day 91a.5 P2D — Q11 R4 cat 3 3-step fallback chain per R-Lock-2.
        headerTooltip: resolveTooltip(bswcActive, 'roi_per_day'),
        valueGetter: (p: ValueGetterParams<GridRow>) => {
          const data = p.data;
          if (!data || isAllChannelsRow(data)) return null;
          return safetyCap ? data.roi_per_day_safety : data.roi_per_day_velocity;
        },
        valueFormatter: (p: ValueFormatterParams<GridRow>) => fmtPct(p.value as number | null),
        cellStyle: monoStyle,
      },
      {
        headerName: 'Sales/Mo',
        colId: 'sales_per_month',
        width: 90,
        minWidth: 90,
        type: 'numericColumn',
        hide: hiddenCols.includes('sales_per_month'),
        headerClass: 'ag-right-aligned-header',
        // Day 91a.5 P2D — Q11 R4 cat 2 4-step fallback chain per R-Lock-2.
        // Admin override via Engine Editor → Weight Components → tooltip_text
        // column populates bswc.tooltip_text; pre-existing hardcoded text
        // preserved as 3rd fallback step (graceful degradation while admin
        // curation pending).
        headerTooltip: resolveTooltip(
          bswcActive,
          'sales_velocity',
          'Amazon US estimated unit sales per month — raw Algopix figure. "—" means no velocity data; the engine falls back to the 30-day default.'
        ),
        valueGetter: (p: ValueGetterParams<GridRow>) => {
          const data = p.data;
          if (!data || isAllChannelsRow(data)) return null;
          return data.products?.amazon_monthly_sales ?? null;
        },
        valueFormatter: (p: ValueFormatterParams<GridRow>) => fmtInt(p.value as number | null),
        cellStyle: monoSecondaryStyle,
      },
      {
        headerName: 'Days/Unit',
        colId: 'days_locked',
        width: 90,
        minWidth: 90,
        type: 'numericColumn',
        hide: hiddenCols.includes('days_locked'),
        headerClass: 'ag-right-aligned-header',
        // Header tooltip explains both modes. In Velocity Mode this equals
        // pure days-to-sell; in Cash Cycle Mode it adds the path
        // payout cycle. The 30-day default kicks in whenever Algopix has
        // no velocity data for the product.
        //
        // Day 91a.5 P2D R-Lock-6 — Days/Unit is a derived display metric;
        // NOT a Buy Score weight component. Mathematical redundancy with
        // profit_per_day (engine: profit_per_unit × monthly_sales / 30) means
        // a separate days_to_sell weight would re-encode the same time
        // dimension. Reversibility intact: future bswc INSERT +
        // applies_to_metric mapping can graduate this to weight component if
        // buyer feedback surfaces fast-turnover-as-distinct-semantic. EXEMPT
        // from bswc-fallback wiring at P2D; hardcoded tooltip preserved per
        // R-Lock-6 A5(b) confirmed-not-bswc lean. See RBP L1005 + Day 91a.5
        // R2 §1 LOAD-BEARING disambiguation + R-Lock-6 lock.
        headerTooltip:
          'Estimated days to sell one unit based on monthly sales velocity. In Cash Cycle Mode this also adds the selectedPath payout cycle. Defaults to 30 days when no velocity data is available.',
        valueGetter: (p: ValueGetterParams<GridRow>) => {
          const data = p.data;
          if (!data || isAllChannelsRow(data)) return null;
          return safetyCap ? data.days_credit_locked_safety : data.days_credit_locked_velocity;
        },
        valueFormatter: (p: ValueFormatterParams<GridRow>) => fmtDays(p.value as number | null),
        cellStyle: monoMutedStyle,
      },
      {
        headerName: 'Floor',
        field: 'effective_floor',
        colId: 'effective_floor',
        width: 80,
        hide: hiddenCols.includes('effective_floor'),
        type: 'numericColumn',
        valueFormatter: (p: ValueFormatterParams<GridRow>) => fmt$(p.value as number | null),
        cellStyle: monoSecondaryStyle,
      },
      {
        headerName: 'Buy Score',
        field: 'buy_score',
        colId: 'buy_score',
        width: 85,
        sortable: true,
        filter: false,
        hide: hiddenCols.includes('buy_score'),
        // Day 91a.5 P2D \u2014 hardcoded composite explainer per build prompt \u00a77
        // (rollup column; not a bswc-key consumer per Q11 R4 + R-Lock-2 scope).
        headerTooltip:
          'Weighted composite of all enabled component metrics. See Engine Editor \u2192 Weight Components to configure components.',
        valueFormatter: (p: ValueFormatterParams<GridRow>) =>
          p.value != null ? Number(p.value).toFixed(1) : '\u2014',
        cellStyle: buyScoreStyle,
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: 'Status',
        colId: '_status',
        width: 90,
        sortable: false,
        filter: false,
        hide: hiddenCols.includes('_status'),
        headerClass: 'ag-center-aligned-header',
        cellStyle: flexCenterCenterStyle,
        cellRenderer: StatusCellSingleChannel,
      },
      // Day 58 — tier ROI%/PPU columns (2 per tier). Empty array when tier
      // mode is disabled. Spread at the tail of the single-channel return so
      // tier columns appear together as a contiguous block after Status.
      ...tierColDefs,
    ];
  }, [
    selectedPath,
    safetyCap,
    hiddenCols,
    // Day 74 P4.2 — activePaths drives both dot column generation
    // (Phase 8) and F35 best-channel valueGetter (Phase 6).
    activePaths,
    // Day 91a.5 P2D — bswcActive drives headerTooltip resolution at 6 sites
    // (Sales/Mo all-channels + single-channel + Profit/Unit + ROI % +
    // Profit/Day + ROI/Day) via resolveTooltip fallback chain per R-Lock-2.
    // Buy Score rollup + Days/Unit + tier-mode T${n} headers EXEMPT.
    bswcActive,
    TitleCellAllChannels,
    TitleCellSingleChannel,
    channelDotRenderers,
    channelRoiRenderers,
    StatusCellAllChannels,
    StatusCellSingleChannel,
    tierColDefs,
  ]);

  // Day 58 — AG Grid React adapter treats the `columnDefs` prop as INITIAL
  // column state on grid mount. Runtime additions/removals (tier mode
  // toggle adding/dropping the 6 tier ColDefs) are NOT applied via prop
  // change alone — explicit setGridOption('columnDefs', …) is required.
  // Mirrors the datasource-reset pattern above. Diagnosed Day 58 e2e Item 7
  // automation: tier columns never appeared in the DOM despite columnDefs
  // useMemo producing 6 tier ColDefs with the correct state.
  useEffect(() => {
    const api = gridApiRef.current;
    if (!api) return;
    api.setGridOption('columnDefs', columnDefs);
  }, [columnDefs]);

  // ── Row click → open detail panel ─────────────────────────

  const onRowClicked = useCallback(
    (event: RowClickedEvent<GridRow>) => {
      const data = event.data;
      if (!data) return;
      // Don't open detail if clicking a button (e.g. ISG SKU copy)
      const domEvent = event.event as Event | undefined;
      const target = domEvent?.target as HTMLElement | null;
      if (target?.closest('button')) return;

      if (isAllChannelsRow(data)) {
        setSelectedProduct(data);
        setSelectedChannels(data._channels || {});
        // Day 47.5 — feed product into Debug Panel context so Shift+Ctrl+D
        // auto-populates with the row the user is inspecting. AllChannelsRow
        // identity is the product_id (joined directly from products).
        setDebugProductId(data.id ?? null);
      } else {
        // Single channel — fetch all channels for this product
        const product = data.products;
        setSelectedProduct(product ?? null);
        setDebugProductId(product?.id ?? null);

        // Build partial channels from current data
        const channels: Record<string, ChannelProfitRow | undefined> = {
          [selectedPath]: data,
        };
        setSelectedChannels(channels);

        // Fetch all channels for this product
        if (product?.id && distributorId) {
          supabase
            .from('product_profitability')
            .select('*')
            .eq('distributor_id', distributorId)
            .eq('product_id', product.id)
            .then(({ data: profRows }) => {
              const rows = (profRows ?? []) as unknown as ChannelProfitRow[];
              const allChannels: Record<string, ChannelProfitRow> = {};
              for (const row of rows) {
                const pathKey = row.marketplace;
                if (pathKey) allChannels[pathKey] = row;
              }
              setSelectedChannels(allChannels);
            });
        }
      }
      setDetailPanelOpen(true);
    },
    [selectedPath, distributorId, setDebugProductId]
  );

  // ── PO tray handlers ──────────────────────────────────────

  // Track AG Grid selection so the bulk action bar knows whether to render
  // and the multi-add flow can snapshot the right rows.
  const handleSelectionChanged = useCallback((): void => {
    const api = gridRef.current?.api;
    if (!api) return;
    setSelectedGridRows(api.getSelectedRows() as GridRow[]);
  }, []);

  const clearGridSelection = useCallback((): void => {
    gridRef.current?.api?.deselectAll();
    setSelectedGridRows([]);
  }, []);

  // Build one MultiAddDraft per selected row. Works for both row shapes:
  // - SingleChannelRow → read cost/isg_sku/title from .products, roi_pct from
  //   the row itself (keyed under the active path).
  // - AllChannelsRow → read cost/isg_sku/title from the top-level, roi_pct
  //   from each entry in _channels.
  const handleOpenMultiAdd = useCallback((): void => {
    if (selectedGridRows.length === 0) return;
    const drafts: MultiAddDraft[] = [];
    for (const row of selectedGridRows) {
      let productId: string | null = null;
      let isgSku: string | null = null;
      let title: string | null = null;
      let cost = 0;
      const roiByPath: Record<string, number | null> = {};
      if (isAllChannelsRow(row)) {
        productId = row.id ?? null;
        isgSku = row.isg_sku ?? null;
        title = row.title ?? null;
        cost = Number(row.cost_usd) || 0;
        for (const [path, ch] of Object.entries(row._channels ?? {})) {
          roiByPath[path] = ch?.roi_pct != null ? Number(ch.roi_pct) : null;
        }
      } else {
        // SingleChannelRow — data comes from the `products` inner join; the
        // top-level `row.isg_sku` is typed `unknown` via the index signature
        // on ChannelProfitRow, so we read only from `row.products` here.
        productId = row.products?.id ?? row.product_id ?? null;
        isgSku = row.products?.isg_sku ?? null;
        title = row.products?.title ?? null;
        // U10 cost re-point — pp.cost_usd (the row's own compute snapshot;
        // select('*')) replaces the products-embed read-copy field.
        cost = Number(row.cost_usd) || 0;
        // Single-channel rows only carry ROI for the active path —
        // the modal header will be fixed to that path anyway.
        if (row.marketplace) {
          roiByPath[row.marketplace] = row.roi_pct != null ? Number(row.roi_pct) : null;
        }
      }
      if (!productId) continue;
      // If this product is already staged, pre-fill the existing quantity so
      // the user can adjust rather than overwrite blindly.
      const existing = poStaging.items.find((i) => i.product_id === productId);
      drafts.push({
        productId,
        isgSku,
        title,
        cost,
        roiByPath,
        quantity: existing?.quantity && existing.quantity > 0 ? existing.quantity : 1,
        wasExisting: !!existing,
      });
    }
    if (drafts.length === 0) return;
    // Seed the modal's path selector. In single-channel mode it stays
    // fixed; in All Channels mode the user can switch.
    let seedPath: string;
    if (selectedPath !== 'all') {
      seedPath = selectedPath;
    } else {
      const activeFirst = activePaths[0]?.path_key as string | undefined;
      seedPath = activeFirst || 'amazon_fba';
    }
    setMultiAddDrafts(drafts);
    setMultiAddMp(seedPath);
    // Single-channel mode pre-confirms (the path is pinned by the
    // active tab — no user choice to make). All Channels requires explicit
    // user interaction with the selector before Submit unlocks.
    setPathConfirmed(selectedPath !== 'all');
    setMultiAddOpen(true);
  }, [selectedGridRows, selectedPath, activePaths, poStaging.items]);

  const handleCloseMultiAdd = useCallback((): void => {
    setMultiAddOpen(false);
    setMultiAddDrafts([]);
    setMultiAddSubmitting(false);
    setPathConfirmed(false);
  }, []);

  const handleMultiAddQtyChange = useCallback((productId: string, qty: number): void => {
    const clamped = Math.max(1, Math.floor(qty) || 1);
    setMultiAddDrafts((prev) =>
      prev.map((d) => (d.productId === productId ? { ...d, quantity: clamped } : d))
    );
  }, []);

  const handleConfirmMultiAdd = useCallback(async (): Promise<void> => {
    if (multiAddDrafts.length === 0 || !multiAddPath) return;
    // Day 81 P5.6 (F53) — gate on activePaths + distributorId before any write.
    // activePaths=[] means Settings has every path deactivated; staging an
    // inactive path would silently land in DB. distributorId=null means no
    // session/active distributor; the F51/F52 substrate requires a value.
    if (activePaths.length === 0 || !distributorId) {
      setToast({
        kind: 'error',
        message:
          activePaths.length === 0
            ? 'No active fulfillment paths — enable one in Settings'
            : 'No distributor selected',
      });
      return;
    }
    setMultiAddSubmitting(true);
    const fulfillmentPath = multiAddPath;
    try {
      // Sequential to avoid a burst of upserts contending on user_id,product_id.
      // Each row is staged with a single-key allocation map so the staging
      // shape is consistent regardless of entry path (multi-add vs DetailPanel).
      // Day 64 F49: aggregate per-item success/failure so the toast surfaces
      // actual counts instead of always claiming success.
      const results: AddPOStagingResult[] = [];
      for (const d of multiAddDrafts) {
        const r = await poStaging.addItem({
          productId: d.productId,
          isgSku: d.isgSku,
          costPerUnit: d.cost,
          quantity: d.quantity,
          distributorId,
          fulfillmentPath,
          allocations: { [fulfillmentPath]: d.quantity },
        });
        results.push(r);
      }
      const outcome = aggregateMultiAddOutcome(results);
      if (outcome.failed === 0) {
        triggerTicker(outcome.message);
      } else {
        setToast({ kind: 'error', message: outcome.message });
      }
      // On any-success path, open tray + clear selection + close modal so the
      // staged items become visible. On full-failure, leave UI state intact —
      // user sees the error toast and can retry without losing the multi-add
      // selection.
      if (outcome.succeeded > 0) {
        setPoTrayOpen(true);
        clearGridSelection();
        handleCloseMultiAdd();
      } else {
        setMultiAddSubmitting(false);
      }
    } catch (e) {
      console.error('[BuyingSession] multi-add error:', e);
      setMultiAddSubmitting(false);
    }
  }, [
    multiAddDrafts,
    multiAddPath,
    poStaging,
    clearGridSelection,
    handleCloseMultiAdd,
    activePaths,
    distributorId,
  ]);

  // DetailPanel "Add to PO" — single-product path. The DetailPanel allocates
  // units across marketplaces (e.g. FBA × 3 + FBM × 5) and passes the full
  // map in `item.allocations`. Until Day 46.5 sub-session 1 this handler
  // dropped that map and hardcoded quantity:1 (B-R2-4 / B-R2-6 — silent data
  // loss). Now: convert path keys → fulfillment_path keys, sum to
  // total qty, pick dominant path (max qty, ties broken by activePaths
  // order), and stage with the full allocation map intact.
  const handleAddToPO = useCallback(
    async (item: AddToPOPayload): Promise<void> => {
      // Day 81 P5.6 (F53) — gate on activePaths + distributorId before any
      // write. Mirrors handleConfirmMultiAdd's gate; activePaths feeds the
      // tiebreaker logic below so an empty set would silently make
      // dominantPath unstable.
      if (activePaths.length === 0 || !distributorId) {
        setToast({
          kind: 'error',
          message:
            activePaths.length === 0
              ? 'No active fulfillment paths — enable one in Settings'
              : 'No distributor selected',
        });
        return;
      }
      // Build the fulfillment_path-keyed allocation map. DetailPanel keys
      // are path strings (amazon_fba/amazon_fbm/...) which are now
      // identity-equivalent to fulfillment_path values post-Day-60.5.
      const allocByPath: Record<string, number> = {};
      for (const [path, qtyStr] of Object.entries(item.allocations || {})) {
        const qty = parseInt(qtyStr, 10);
        if (!Number.isFinite(qty) || qty <= 0) continue;
        allocByPath[path] = (allocByPath[path] || 0) + qty;
      }
      const totalQty = Object.values(allocByPath).reduce((s, v) => s + v, 0);
      if (totalQty === 0) {
        triggerTicker('Set quantity for at least one path before adding to PO');
        return;
      }
      // Day 81 P5.6 — F53 fallback chain today is dominantPath (computed
      // from allocations). Future Goflow #12 (BACKLOG L977 —
      // products.default_fulfillment_path_key) would insert here as a
      // per-product tier between dominantPath and activePaths[0]. F53
      // substrate compatible — no re-touch needed when #12 lands.
      //
      // Dominant path = max-quantity path, with activePaths order as the
      // deterministic tiebreaker so the legacy fulfillment_path column
      // settles on a stable value across submissions.
      const orderedPathKeys = activePaths.map((p) => p.path_key);
      let dominantPath = Object.keys(allocByPath)[0];
      for (const path of orderedPathKeys) {
        const candidate = allocByPath[path];
        if (candidate === undefined) continue;
        if (candidate > (allocByPath[dominantPath] || 0)) dominantPath = path;
      }
      // Day 64 F49: await the result + surface error toast on failure instead
      // of fire-and-forget. Don't open the PO tray when nothing staged.
      const result = await poStaging.addItem({
        productId: item.productId,
        isgSku: item.isgSku,
        costPerUnit: item.unitCost,
        quantity: totalQty,
        distributorId,
        fulfillmentPath: dominantPath,
        allocations: allocByPath,
      });
      if (!result.success) {
        setToast({
          kind: 'error',
          message: `Failed to add ${item.isgSku ?? 'item'} to PO`,
        });
        return;
      }
      setPoTrayOpen(true);
    },
    [poStaging, activePaths, distributorId]
  );

  // ── Session param handlers ───────────────────────────────

  const handleFiltersChange = useCallback((key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleWeightsChange = useCallback((key: string, action: WeightAction, value?: string) => {
    setWeights((prev) => {
      const current: WeightState = prev[key] || { enabled: false, weight: '' };
      if (action === 'toggle') {
        return { ...prev, [key]: { ...current, enabled: !current.enabled } };
      }
      if (action === 'weight') {
        return { ...prev, [key]: { ...current, weight: value ?? '' } };
      }
      return prev;
    });
  }, []);

  const handleConstraintsChange = useCallback((key: string, value: string) => {
    setConstraints((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleResetFilters = useCallback(() => setFilters({}), []);
  const handleResetWeights = useCallback(() => setWeights({}), []);
  const handleResetAll = useCallback(() => {
    setFilters({});
    setWeights({});
    setConstraints({});
  }, []);

  const handleApplyScenario = useCallback(
    (scenarioId: string | null) => {
      if (!scenarioId) {
        // "(none)/Custom" — dissociate. Leave current params alone.
        setActiveScenarioId(null);
        return;
      }
      const scenario = scenarios.find((s) => s.id === scenarioId);
      if (!scenario) return;
      // Day 73 P4.1 C.9 — filter stale keys before applying. Surface a
      // toast if any keys were dropped so the user knows the loaded
      // scenario differs from its saved shape.
      const { cleaned, dropped } = filterScenarioToCurrentKeys(scenario, validKeys);
      const totalDropped =
        dropped.filterKeys.length + dropped.weightKeys.length + dropped.constraintKeys.length;
      setFilters(cleaned.filters);
      setWeights(cleaned.weights);
      setConstraints(cleaned.constraints);
      setActiveScenarioId(scenarioId);
      if (totalDropped > 0) {
        const plural = totalDropped === 1 ? '' : 's';
        setToast({
          kind: 'success',
          message: `Applied "${scenario.name}" — ${totalDropped} unused field${plural} dropped`,
        });
      }
      // The debounced writeback effect picks up all four state changes and
      // persists the patch — no explicit updateUiState call needed here.
    },
    [scenarios, validKeys]
  );

  // Day 73 P4.1 BACKLOG L427 — soft drift: when current state diverges
  // from the active scenario's saved values, display the dropdown as
  // "Custom (none)" without clearing activeScenarioId in persisted state.
  // Re-selecting the same scenario in the dropdown restores its values.
  const displayedScenarioId = useMemo<string | null>(() => {
    if (!activeScenarioId) return null;
    const active = scenarios.find((s) => s.id === activeScenarioId);
    if (!active) return null;
    return scenarioMatchesState(active, { filters, weights, constraints }, paramDefaults, validKeys)
      ? activeScenarioId
      : null;
  }, [activeScenarioId, scenarios, filters, weights, constraints, paramDefaults, validKeys]);

  const handleSavePreset = useCallback(() => {
    setSavePresetName('');
    setSavePresetScope('current');
    setSavePresetError(null);
    setSavePresetOpen(true);
  }, []);

  const handleSavePresetConfirm = useCallback(async () => {
    const trimmed = savePresetName.trim();
    if (!trimmed) {
      setSavePresetError('Name is required');
      return;
    }
    setSavePresetSubmitting(true);
    setSavePresetError(null);
    try {
      const scopedDistributorId = savePresetScope === 'current' ? distributorId : null;
      // Day 73 P4.1 C.9 — silent filter on save. Current state shouldn't
      // contain stale keys outside of a per-user prefs schema change;
      // rare and self-correcting, no toast.
      const { cleaned } = filterScenarioToCurrentKeys({ filters, weights, constraints }, validKeys);
      await saveScenario({
        name: trimmed,
        distributorId: scopedDistributorId,
        filters: cleaned.filters,
        weights: cleaned.weights,
        constraints: cleaned.constraints,
      });
      setSavePresetOpen(false);
      setToast({ kind: 'success', message: `Saved scenario "${trimmed}"` });
    } catch (e) {
      setSavePresetError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSavePresetSubmitting(false);
    }
  }, [
    savePresetName,
    savePresetScope,
    distributorId,
    filters,
    weights,
    constraints,
    saveScenario,
    validKeys,
  ]);

  const handleSaveAsDraft = useCallback(() => {
    // Day 80 P5.5 L560 — clear stale success toast before modal opens so
    // a prior "Saved draft" toast can't bleed into the new save attempt.
    setToast(null);
    setSaveAsDraftName(session?.name ?? '');
    setSaveAsDraftError(null);
    setSaveAsDraftOpen(true);
  }, [session?.name]);

  const handleSaveAsDraftConfirm = useCallback(async () => {
    const trimmed = saveAsDraftName.trim();
    if (!trimmed) {
      setSaveAsDraftError('Name is required');
      return;
    }
    setSaveAsDraftSubmitting(true);
    setSaveAsDraftError(null);
    try {
      // Day 76 P5.1 Phase 5 — re-capture param_snapshot at draft-save per
      // Sol option (a) lock 2026-05-17. Reuses existing paramStoreForDrift
      // useParamStore mount (Day 73 P4.1 β-extraction); no new mount needed.
      const paramSnapshot = buildParamSnapshot(
        paramStoreForDrift.allParams,
        distributorId
      ) as unknown as Record<string, unknown>;
      await saveAsDraft(trimmed, paramSnapshot);
      setSaveAsDraftOpen(false);
      setToast({ kind: 'success', message: `Saved draft "${trimmed}"` });
    } catch (e) {
      setSaveAsDraftError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaveAsDraftSubmitting(false);
    }
  }, [saveAsDraftName, saveAsDraft, paramStoreForDrift, distributorId]);

  // Day 76 P5.1 B.3 — handleSaveDefaults UPSERT-by-key per OQ5 (a) lock.
  // Closes Audit #1 F13 / Day 55 honesty-edit TODO. Preserves visibility/
  // order from Settings → BSP; updates `default` field for current filter/
  // weight/constraint values. Weight semantic: ENABLED-only per Sol lock.
  const handleSaveDefaults = useCallback(async () => {
    if (!user?.id) {
      setToast({ kind: 'error', message: 'Cannot save defaults: not signed in' });
      return;
    }
    try {
      // Day 98.5 F5 — funnel through mergePrefs (idempotent here: the payload is
      // already full; the funnel is the guard so this writer stays drop-proof).
      const payload = mergePrefs(
        buyingSessionPrefs,
        buildSaveDefaultsPayload(
          bswcActive,
          buyingSessionPrefs,
          filters,
          weights,
          constraints,
          guardrails
        )
      );
      const { error: e } = await supabase
        .from('profiles')
        .update({ buying_session_prefs: payload as unknown as Json })
        .eq('id', user.id);
      if (e) throw new Error(e.message);
      // Optimistically update AppContext so subsequent renders + new sessions
      // see the saved values immediately (no refetch round-trip needed).
      setBuyingSessionPrefs(payload);
      setToast({ kind: 'success', message: 'Saved current values as your defaults' });
    } catch (e) {
      setToast({
        kind: 'error',
        message: e instanceof Error ? e.message : 'Failed to save defaults',
      });
    }
  }, [
    user?.id,
    bswcActive,
    buyingSessionPrefs,
    filters,
    weights,
    constraints,
    guardrails,
    setBuyingSessionPrefs,
  ]);

  // ── Day 98.1b §11 — Risk Factors config (Option B auto-persist) ───
  // Risk is a stable, restock-like preference (not a per-session/per-PO knob):
  // each change (1) optimistically + SYNCHRONOUSLY updates AppContext so a fast
  // "Build PO" within the debounce window reads fresh prefs.risk (POBuilder reads
  // useApp().buyingSessionPrefs; the provider sits above the router, so the set
  // propagates across the nav hop) and (2) debounces a FULL-prefs DB write —
  // spreading live prefs + overriding risk, never restock's partial merge that
  // would clobber saved constraints/guardrails. No ui_state / nav-state / scenario
  // / chip — prefs.risk is the single home; POBuilder's resolvedRisk reads it.
  const riskConfig = buyingSessionPrefs?.risk ?? DEFAULT_RISK_CONFIG;
  const latestPrefsRef = useRef<BuyingSessionPrefs | null>(buyingSessionPrefs);
  useEffect(() => {
    latestPrefsRef.current = buyingSessionPrefs;
  }, [buyingSessionPrefs]);
  const riskPersistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (riskPersistTimer.current) clearTimeout(riskPersistTimer.current);
    },
    []
  );

  const persistRisk = useCallback(
    (nextRisk: BuyingSessionRiskPref) => {
      // (1) optimistic, synchronous — guarantees the cross-nav read is fresh.
      // Day 98.5 F5 — mergePrefs full-spread (never clobbers other prefs keys).
      setBuyingSessionPrefs(mergePrefs(latestPrefsRef.current, { risk: nextRisk }));
      // (2) debounced full-prefs DB write (250ms — mirrors useRestockForecast).
      if (!user?.id) return;
      if (riskPersistTimer.current) clearTimeout(riskPersistTimer.current);
      const uid = user.id;
      riskPersistTimer.current = setTimeout(() => {
        // Re-read latest prefs at flush so a concurrent save-defaults isn't lost.
        const flush: BuyingSessionPrefs = mergePrefs(latestPrefsRef.current, { risk: nextRisk });
        void supabase
          .from('profiles')
          .update({ buying_session_prefs: flush as unknown as Json })
          .eq('id', uid)
          .then(({ error: e }) => {
            if (e) setToast({ kind: 'error', message: 'Failed to save risk settings' });
          });
      }, 250);
    },
    [user?.id, setBuyingSessionPrefs]
  );

  const handleRiskFactorToggle = useCallback(
    (key: RiskFactorKey, enabled: boolean) => {
      const current = latestPrefsRef.current?.risk ?? DEFAULT_RISK_CONFIG;
      persistRisk({ ...current, factors: { ...current.factors, [key]: { enabled } } });
    },
    [persistRisk]
  );

  const handleRiskThresholdChange = useCallback(
    (which: 'velocity_floor' | 'competition_ceiling', value: string) => {
      const current = latestPrefsRef.current?.risk ?? DEFAULT_RISK_CONFIG;
      const num = Number(value);
      const parsed = value !== '' && Number.isFinite(num) ? num : null;
      persistRisk({ ...current, thresholds: { ...current.thresholds, [which]: parsed } });
    },
    [persistRisk]
  );

  // ── Change path/filter → refresh grid ──────────────

  const changePath = useCallback((path: PathSelection) => {
    setSelectedPath(path);
    // Selections made on the previous tab are not meaningful after a tab
    // switch — the row set and active path context both change.
    gridRef.current?.api?.deselectAll();
    setSelectedGridRows([]);
  }, []);

  const changeFilterMode = useCallback((mode: FilterMode) => {
    setFilterMode(mode);
  }, []);

  // ── Dismiss filter chip ───────────────────────────────────

  // Day 77 P5.2 B.4 — chip dismiss handlers. dismissChip only handles
  // source='manual' keys (sidebar chips have no X button per ADR Decision 44).
  const dismissChip = useCallback((key: string) => {
    switch (key) {
      case 'brand':
        setSelectedBrand(null);
        break;
      case 'manufacturer':
        setSelectedMfrIds(new Set());
        break;
      case 'filter-mode':
        setOnlyBranded(false);
        setMatchedBrandsOnly(false);
        break;
    }
  }, []);

  const clearManualChips = useCallback(() => {
    setSelectedBrand(null);
    setSelectedMfrIds(new Set());
    setOnlyBranded(false);
    setMatchedBrandsOnly(false);
  }, []);

  // ── AG Grid options ───────────────────────────────────────

  const defaultColDef = useMemo<ColDef<GridRow>>(
    () => ({
      sortable: true,
      resizable: true,
      sortingOrder: ['asc', 'desc', null],
      unSortIcon: true,
      suppressHeaderMenuButton: false,
      headerClass: 'ag-left-aligned-header',
      cellStyle: { textAlign: 'left' } as CellStyle,
      valueFormatter: emptyFormatter,
    }),
    []
  );

  // AG Grid v35 Theming API — use themeQuartz with dark/light params
  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'var(--surface-2)',
        headerBackgroundColor: 'var(--surface-3)',
        oddRowBackgroundColor: 'var(--surface-2)',
        rowHoverColor: 'var(--surface-3)',
        selectedRowBackgroundColor: 'var(--accent-muted)',
        borderColor: 'var(--border)',
        headerTextColor: 'var(--ink-muted)',
        textColor: 'var(--ink-primary)',
        rowBorder: { color: 'var(--border-subtle)' },
        fontFamily: "'Inter', system-ui, sans-serif",
        fontSize: '12px',
        headerHeight: 36,
        rowHeight: 36,
        spacing: 4,
      }),
    []
  );

  // Day 80 P5.5 L528 + L588 — loading gate. Two conditions trigger:
  // (1) sessionLoading true during the auto-load roundtrip; (2) session
  // exists but ui_state hydration hasn't run yet. The (session && !hydrated)
  // form deliberately excludes the no-sessionId case (new-session URL) so
  // the page renders its empty state instead of looping on Loading.
  if (sessionLoading || (session && !hydrated)) {
    return (
      <div className="flex flex-col h-full -m-4 items-center justify-center text-ink-muted text-xs">
        Loading session…
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full -m-4">
      {/* ── Toolbar ─────────────────────────────────────────── */}
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        {/* Top row: title + recalculate */}
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-base font-semibold text-ink-bright flex items-center gap-2">
              <TrendingUp size={16} className="text-accent" />
              Buying Session
              <DistributorSelector />
            </h1>
            <p
              className="text-2xs text-ink-muted font-mono mt-0.5"
              title={calcTimestamp ? new Date(calcTimestamp).toLocaleString() : undefined}
            >
              {calcTimestamp
                ? `Last calculated: ${relativeTime(calcTimestamp)}`
                : 'Not yet calculated this session'}
            </p>
            {/* DD-08: cache-staleness hint — a product input changed since buy scores were computed. */}
            {buyScoreStale && (
              <p
                className="text-2xs text-warning font-mono mt-0.5"
                title="A product input changed after the buy scores were last computed for this distributor. Recalculate to refresh them."
              >
                ⚠ Inputs changed since buy scores computed — recalc
              </p>
            )}
          </div>

          {/* PO Impact snapshot — admin only, hidden when staging empty.
              Day 72 P3.5: 4 pills (Items / Cost / Est. Profit / ROI%) sourced from
              calculate_po_impact_regrounded RPC (allocation-honoring). Hover reveals sparse
              per-path breakdown. ROI% uses raw-cost denominator (operator outlay-
              return form) — see migration COMMENT for the documented divergence
              from engine roi_pct column. */}
          {isAdmin && poStaging.items.length > 0 && poImpact && (
            <div
              className="relative flex items-center gap-3 px-3 border-l border-r border-border mx-2 cursor-default"
              onMouseEnter={() => setPoImpactHover(true)}
              onMouseLeave={() => setPoImpactHover(false)}
              aria-label="PO Impact summary — hover for per-path breakdown"
            >
              <div className="flex flex-col items-center leading-tight">
                <span className="text-2xs text-ink-muted uppercase tracking-wider">Items</span>
                <span className="text-xs font-semibold font-mono text-ink-bright">
                  {poImpact.items}
                </span>
              </div>
              <div className="flex flex-col items-center leading-tight">
                <span className="text-2xs text-ink-muted uppercase tracking-wider">Cost</span>
                <span className="text-xs font-semibold font-mono text-ink-bright">
                  {poImpact.cost != null
                    ? `$${poImpact.cost.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                    : '—'}
                </span>
              </div>
              <div className="flex flex-col items-center leading-tight">
                <span className="text-2xs text-ink-muted uppercase tracking-wider">
                  Est. Profit
                </span>
                <span
                  className={clsx(
                    'text-xs font-semibold font-mono',
                    poImpact.est_profit == null
                      ? 'text-ink-muted'
                      : poImpact.est_profit >= 0
                        ? 'text-success'
                        : 'text-danger'
                  )}
                >
                  {poImpact.est_profit != null
                    ? `$${Math.abs(poImpact.est_profit).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                      })}`
                    : '—'}
                </span>
              </div>
              <div
                className="flex flex-col items-center leading-tight"
                title="Outlay-return ROI% — engine profit ÷ raw outlay (cash-on-cash on what you actually pay). Distinct from the grid's ROI% (landed), which divides by fully-loaded landed cost."
              >
                {/* FN-4 P-2: this is the OUTLAY-basis ROI (raw-cost denominator); the
                    BuyingSession grid roi_pct column is the LANDED basis. */}
                <span className="text-2xs text-ink-muted uppercase tracking-wider">
                  ROI% (outlay)
                </span>
                <span
                  className={clsx(
                    'text-xs font-semibold font-mono',
                    poImpact.roi_pct !== null && poImpact.roi_pct >= 0
                      ? 'text-success'
                      : 'text-danger'
                  )}
                >
                  {fmtPct(poImpact.roi_pct)}
                </span>
              </div>

              {/* Day 81 P5.6 (F52) — cross-distributor disclaimer. Renders
                  directly under the pill cluster when the RPC reports
                  excluded rows so the operator knows their tray has items
                  not contributing to this session's PO Impact aggregates.
                  Italic-muted to stay quiet when present + 0-byte when
                  count is 0 (no DOM at all). */}
              {poImpact.cross_distributor_excluded_count > 0 && (
                <div
                  className="absolute left-3 top-full mt-1 text-2xs text-ink-muted italic"
                  data-testid="po-impact-cross-distributor-disclaimer"
                >
                  {poImpact.cross_distributor_excluded_count} item
                  {poImpact.cross_distributor_excluded_count !== 1 ? 's' : ''} from other
                  distributors not included
                </div>
              )}

              {/* Hover tooltip — sparse per-path breakdown sorted by profit desc.
                  Controlled-state pattern per src/components/layout/Sidebar.tsx
                  Tooltip L216-238 precedent. Absolute-positioned BELOW the pill
                  cluster (toolbar is at top of page; above would clip viewport). */}
              {poImpactHover && Object.keys(poImpact.per_path).length > 0 && (
                <div
                  role="tooltip"
                  className="absolute left-0 top-full mt-2 z-50 bg-surface-1 border border-border rounded shadow-lg p-3 min-w-[280px]"
                >
                  <div className="text-2xs uppercase tracking-wider text-ink-muted mb-2">
                    Per-Path Breakdown
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-2xs text-ink-muted">
                        <th className="text-left font-normal pb-1">Path</th>
                        <th className="text-right font-normal pb-1">Units</th>
                        <th className="text-right font-normal pb-1">Profit</th>
                        <th className="text-right font-normal pb-1">ROI%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(poImpact.per_path)
                        // Kind-4: unknown-profit paths (NULL) sink to the
                        // bottom via a finite sentinel (NaN-free comparator).
                        .sort(([, a], [, b]) => (b.profit ?? -1e18) - (a.profit ?? -1e18))
                        .map(([path, row]) => (
                          <tr key={path} className="border-t border-border-subtle">
                            <td className="text-left py-1 text-ink-primary">
                              {pathLabel[path] || path}
                            </td>
                            <td className="text-right py-1 font-mono text-ink-primary">
                              {row.units}
                            </td>
                            <td
                              className={clsx(
                                'text-right py-1 font-mono',
                                row.profit == null
                                  ? 'text-ink-muted'
                                  : row.profit >= 0
                                    ? 'text-success'
                                    : 'text-danger'
                              )}
                            >
                              {row.profit != null
                                ? `$${Math.abs(row.profit).toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                  })}`
                                : '—'}
                            </td>
                            <td
                              className={clsx(
                                'text-right py-1 font-mono',
                                row.roi_pct !== null && row.roi_pct >= 0
                                  ? 'text-success'
                                  : 'text-danger'
                              )}
                            >
                              {fmtPct(row.roi_pct)}
                            </td>
                          </tr>
                        ))}
                      {/* Day 81 P5.6 (F52) — tooltip footer echoing the
                          disclaimer so the operator hovering for the
                          per-path breakdown sees the cross-distributor
                          context inline with the data. */}
                      {poImpact.cross_distributor_excluded_count > 0 && (
                        <tr className="border-t border-border">
                          <td
                            colSpan={4}
                            className="pt-2 text-2xs italic text-ink-muted text-center"
                          >
                            {poImpact.cross_distributor_excluded_count} cross-distributor item
                            {poImpact.cross_distributor_excluded_count !== 1 ? 's' : ''} excluded
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={() => navigate('/purchasing/buying-session')}
            className="arbi-btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5 flex-shrink-0"
            data-testid="new-session-button"
            aria-label="Start a new buying session"
          >
            <Plus size={12} />
            New Session
          </button>

          {/* Day 99a #DBN-P1-1: hide Recalculate for viewer (V-ROUI) — calculate_profitability
              is DB-gated to 42501 for viewer (cache-mutating recompute). */}
          {!isViewer && (
            <button
              onClick={recalculate}
              disabled={recalculating || !distributorId}
              className="arbi-btn-primary text-xs px-3 py-1.5 flex items-center gap-1.5"
            >
              <RefreshCw size={12} className={recalculating ? 'animate-spin' : ''} />
              {recalculating ? 'Calculating…' : 'Recalculate'}
            </button>
          )}

          {/* Day 76 P5.1 Phase 8D — Save as Draft moved from toolbar row 2 to
              row 1 alongside New Session + Recalculate as session-level
              actions per UI_STANDARDS.md §8 (Day 55+ BACKLOG follow-up).
              Day 64.5 V-ROUI: hidden for viewer. */}
          {!isViewer && (
            <button
              type="button"
              onClick={handleSaveAsDraft}
              disabled={!session}
              className="arbi-btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="save-as-draft-button"
              aria-label="Save buying session as draft"
            >
              <Save size={12} />
              Save as Draft
            </button>
          )}
          {Object.values(weights).some((w) => !!w && w.enabled && parseFloat(w.weight) > 0) && (
            <span className="text-2xs text-ink-muted ml-2">Score reflects last recalculate</span>
          )}
        </div>

        {/* Controls row — single continuous row with separators */}
        <div className="flex items-center gap-0 overflow-x-auto">
          {/* Path tabs */}
          <div
            role="tablist"
            aria-label="Fulfillment path"
            className="flex items-center bg-surface-2 rounded-lg p-0.5 gap-0.5 border border-border-subtle flex-shrink-0"
          >
            {TABS.map((tab) => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={selectedPath === tab.id}
                aria-label={tab.label}
                onClick={() => changePath(tab.id)}
                className={clsx(
                  'px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
                  selectedPath === tab.id
                    ? 'bg-accent text-white shadow-sm'
                    : 'text-ink-muted hover:text-ink-secondary'
                )}
              >
                {tab.short}
              </button>
            ))}
          </div>

          <div className="w-px h-4 bg-border mx-2 flex-shrink-0" />

          {/* Safety Cap toggle */}
          <button
            type="button"
            role="switch"
            aria-checked={safetyCap}
            aria-label={safetyCap ? 'Switch to Velocity mode' : 'Switch to Cash Cycle mode'}
            onClick={() => setSafetyCap((v) => !v)}
            className="flex items-center gap-1.5 text-xs arbi-btn-ghost px-2 py-1 flex-shrink-0"
            title={
              safetyCap
                ? 'Cash Cycle Mode: Days to Sell + Payout Cycle'
                : 'Velocity Mode: Days to Sell only'
            }
          >
            {safetyCap ? (
              <ToggleRight size={16} className="text-accent" />
            ) : (
              <ToggleLeft size={16} className="text-ink-muted" />
            )}
            <span className={safetyCap ? 'text-accent' : 'text-ink-muted'}>
              {safetyCap ? 'Cash Cycle' : 'Velocity'}
            </span>
          </button>

          <div className="w-px h-4 bg-border mx-2 flex-shrink-0" />

          {/* Quick filter */}
          <div className="flex items-center bg-surface-2 rounded-lg p-0.5 gap-0.5 border border-border-subtle flex-shrink-0">
            {(['all', 'profitable', 'meets_roi'] as const).map((id) => (
              <button
                key={id}
                onClick={() => changeFilterMode(id)}
                className={clsx(
                  'px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
                  filterMode === id
                    ? 'bg-surface-4 text-ink-bright'
                    : 'text-ink-muted hover:text-ink-secondary'
                )}
                aria-pressed={filterMode === id}
              >
                {id === 'all' ? 'All' : id === 'profitable' ? 'Profitable' : 'Meets ROI'}
              </button>
            ))}
          </div>

          <div className="w-px h-4 bg-border mx-2 flex-shrink-0" />

          {/* Sort buttons */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-2xs text-ink-muted uppercase mr-1">Sort</span>
            {(
              [
                { id: 'roi_pct', label: 'ROI% (landed)' }, // FN-4 P-2 straggler (exit-gate finding): mirrors the TOGGLEABLE_COLS roi_pct label — same landed-cost denominator
                { id: 'profit_per_unit', label: 'Profit/Unit' },
                { id: 'profit_per_day', label: 'Profit/Day' },
                { id: 'roi_per_day', label: 'ROI/Day' },
                { id: 'buy_score', label: 'Buy Score' },
              ] as const
            ).map((s) => (
              <button
                key={s.id}
                onClick={() => {
                  const next: SortKey | null = activeSort === s.id ? null : s.id;
                  setActiveSort(next);
                  if (gridApiRef.current) {
                    if (next) {
                      gridApiRef.current.applyColumnState({
                        state: [{ colId: next, sort: 'desc' }],
                        defaultState: { sort: null },
                      });
                    } else {
                      gridApiRef.current.applyColumnState({ defaultState: { sort: null } });
                    }
                  }
                }}
                aria-pressed={activeSort === s.id}
                aria-label={`Sort by ${s.label}`}
                className={clsx(
                  'px-2 py-0.5 text-2xs font-medium rounded transition-colors',
                  activeSort === s.id
                    ? 'text-accent border-b border-accent'
                    : 'text-ink-muted hover:text-ink-secondary'
                )}
                title={selectedPath === 'all' ? 'Sorts loaded rows only' : undefined}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Column sets */}
          {selectedPath !== 'all' && (
            <>
              <div className="w-px h-4 bg-border mx-2 flex-shrink-0" />
              <div className="relative flex-shrink-0">
                <button
                  onClick={() => setColumnsOpen((v) => !v)}
                  className={clsx(
                    'flex items-center gap-1 px-2 py-0.5 text-2xs font-medium rounded transition-colors',
                    columnsOpen
                      ? 'bg-surface-4 text-ink-bright'
                      : 'text-ink-muted hover:text-ink-secondary'
                  )}
                  aria-label="Column visibility"
                  aria-expanded={columnsOpen}
                >
                  <Columns3 size={12} />
                  Columns
                  {hiddenCols.length > 0 && (
                    <span className="ml-0.5 px-1 py-0 rounded-full bg-accent/20 text-accent text-2xs font-mono">
                      {hiddenCols.length}
                    </span>
                  )}
                </button>
                {columnsOpen && (
                  <div className="absolute top-full left-0 mt-1 w-56 bg-surface-2 border border-border rounded-lg shadow-lg z-[100] py-1">
                    {/* Presets */}
                    {columnPresets.length > 0 && (
                      <div className="px-2 py-1 border-b border-border-subtle">
                        <p className="text-2xs text-ink-muted uppercase tracking-wider mb-1">
                          Presets
                        </p>
                        {columnPresets.map((p) => (
                          <div key={p.name} className="flex items-center justify-between py-0.5">
                            <button
                              onClick={() => {
                                applyColumnPreset(p);
                                setColumnsOpen(false);
                              }}
                              className="text-xs text-ink-primary hover:text-accent transition-colors cursor-pointer truncate flex-1 text-left"
                            >
                              {p.name}
                            </button>
                            <button
                              onClick={() => deleteColumnPreset(p.name)}
                              className="p-0.5 text-ink-muted hover:text-danger transition-colors cursor-pointer"
                              aria-label={`Delete preset ${p.name}`}
                            >
                              <Trash2 size={10} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Default reset */}
                    <button
                      onClick={() => {
                        resetColumns();
                        setColumnsOpen(false);
                      }}
                      className="w-full text-left px-2 py-1 text-xs text-ink-secondary hover:bg-surface-3 transition-colors cursor-pointer"
                    >
                      Default (show all)
                    </button>
                    <div className="border-t border-border-subtle my-1" />
                    {/* Column toggles */}
                    {TOGGLEABLE_COLS.map((col) => (
                      <label
                        key={col.id}
                        className="flex items-center gap-2 px-2 py-0.5 hover:bg-surface-3 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={!hiddenCols.includes(col.id)}
                          onChange={() => toggleColumn(col.id)}
                          className="accent-[var(--accent)]"
                        />
                        <span className="text-xs text-ink-primary">{col.label}</span>
                      </label>
                    ))}
                    {/* Save as preset */}
                    <div className="border-t border-border-subtle mt-1 px-2 py-1.5">
                      <div className="flex items-center gap-1">
                        <input
                          value={presetName}
                          onChange={(e) => setPresetName(e.target.value)}
                          placeholder="Preset name..."
                          aria-label="Column preset name"
                          className="arbi-input text-xs py-0.5 px-2 flex-1"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveColumnPreset();
                          }}
                        />
                        <button
                          onClick={saveColumnPreset}
                          disabled={!presetName.trim()}
                          className="arbi-btn-ghost text-2xs px-1.5 py-0.5"
                          aria-label="Save column preset"
                        >
                          <Save size={10} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Day 76 P5.1 Phase 8D — Save as Draft was here in row 2;
              relocated to row 1 alongside New Session + Recalculate per
              UI_STANDARDS.md §8. See the row 1 block above. */}

          {/* Brand filter — mode buttons (All / Only Branded / Matched Brands)
              always render; the per-brand chips only render when the Directory
              → Brands table has entries. Previously the whole block was gated
              on brandList.length > 0, so an empty brands table hid the filter
              entirely (including the always-useful Matched Brands toggle). */}
          <div className="w-px h-4 bg-border mx-2 flex-shrink-0" />
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => {
                setSelectedBrand(null);
                setOnlyBranded(false);
                setMatchedBrandsOnly(false);
              }}
              aria-pressed={!selectedBrand && !onlyBranded && !matchedBrandsOnly}
              className={clsx(
                'px-2 py-0.5 text-2xs font-medium rounded transition-colors',
                !selectedBrand && !onlyBranded && !matchedBrandsOnly
                  ? 'bg-surface-4 text-ink-bright'
                  : 'text-ink-muted hover:text-ink-secondary'
              )}
            >
              All Brands
            </button>
            <button
              onClick={() => {
                setOnlyBranded((v) => !v);
                setSelectedBrand(null);
                setMatchedBrandsOnly(false);
              }}
              aria-pressed={onlyBranded && !selectedBrand}
              className={clsx(
                'px-2 py-0.5 text-2xs font-medium rounded transition-colors',
                onlyBranded && !selectedBrand
                  ? 'bg-accent/15 text-accent'
                  : 'text-ink-muted hover:text-ink-secondary'
              )}
            >
              Only Branded
            </button>
            <button
              onClick={() => {
                setMatchedBrandsOnly((v) => !v);
                setSelectedBrand(null);
                setOnlyBranded(false);
              }}
              aria-pressed={matchedBrandsOnly}
              disabled={brandList.length === 0}
              className={clsx(
                'px-2 py-0.5 text-2xs font-medium rounded transition-colors',
                matchedBrandsOnly
                  ? 'bg-accent/15 text-accent'
                  : 'text-ink-muted hover:text-ink-secondary',
                brandList.length === 0 && 'opacity-40 cursor-not-allowed'
              )}
              title={
                brandList.length === 0
                  ? 'Add entries to Directory → Brands to enable this filter'
                  : 'Show only products whose brand matches Directory → Brands'
              }
            >
              Matched Brands
            </button>
            {brandList.length > 0 && (
              <>
                {brandList.slice(0, 8).map((name) => (
                  <button
                    key={name}
                    onClick={() => {
                      setSelectedBrand((prev) => (prev === name ? null : name));
                      setOnlyBranded(false);
                    }}
                    aria-pressed={selectedBrand === name}
                    aria-label={`Filter by brand ${name}`}
                    className={clsx(
                      'px-2 py-0.5 text-2xs font-medium rounded transition-colors truncate max-w-[80px]',
                      selectedBrand === name
                        ? 'bg-accent text-white'
                        : 'text-ink-muted hover:text-ink-secondary'
                    )}
                    title={name}
                  >
                    {name}
                  </button>
                ))}
                {brandList.length > 8 && (
                  <span className="text-2xs text-ink-muted">+{brandList.length - 8}</span>
                )}
              </>
            )}
          </div>

          {/* Manufacturer multi-select — filters the grid to products whose
              brand belongs to any of the selected manufacturers. Disabled
              when the brands or manufacturers tables are empty (same pattern
              as Matched Brands). */}
          <div className="w-px h-4 bg-border mx-2 flex-shrink-0" />
          <div className="relative flex-shrink-0">
            <button
              type="button"
              onClick={() => setMfrDropdownOpen((v) => !v)}
              disabled={manufacturers.length === 0}
              aria-pressed={selectedMfrIds.size > 0}
              aria-expanded={mfrDropdownOpen}
              title={
                manufacturers.length === 0
                  ? 'Add manufacturers with assigned brands in Directory → Manufacturers to enable this filter'
                  : 'Filter products by manufacturer'
              }
              className={clsx(
                'px-2 py-0.5 text-2xs font-medium rounded transition-colors flex items-center gap-1',
                selectedMfrIds.size > 0
                  ? 'bg-accent/15 text-accent'
                  : 'text-ink-muted hover:text-ink-secondary',
                manufacturers.length === 0 && 'opacity-40 cursor-not-allowed'
              )}
            >
              Manufacturers
              {selectedMfrIds.size > 0 && (
                <span className="text-2xs font-mono">({selectedMfrIds.size})</span>
              )}
              <ChevronDown
                size={10}
                style={{
                  transform: mfrDropdownOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 150ms',
                }}
              />
            </button>
            {mfrDropdownOpen && manufacturers.length > 0 && (
              <>
                {/* Backdrop to capture outside clicks */}
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setMfrDropdownOpen(false)}
                  aria-hidden="true"
                />
                <div
                  role="dialog"
                  aria-label="Select manufacturers"
                  className="absolute top-full left-0 mt-1 z-50 min-w-[240px] max-h-[360px] overflow-y-auto bg-surface-2 border border-border rounded-lg shadow-2xl p-1"
                >
                  {manufacturers.map((m) => {
                    const checked = selectedMfrIds.has(m.id);
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => {
                          setSelectedMfrIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(m.id)) next.delete(m.id);
                            else next.add(m.id);
                            return next;
                          });
                        }}
                        className={clsx(
                          'w-full flex items-center justify-between gap-2 px-2 py-1 rounded text-xs transition-colors',
                          checked
                            ? 'bg-accent/15 text-accent'
                            : 'hover:bg-surface-3 text-ink-primary'
                        )}
                      >
                        <span className="flex items-center gap-1.5 min-w-0">
                          <span
                            className={clsx(
                              'w-3 h-3 rounded border flex items-center justify-center flex-shrink-0',
                              checked ? 'bg-accent border-accent' : 'border-border bg-surface-3'
                            )}
                          >
                            {checked && <Check size={9} className="text-white" />}
                          </span>
                          <span className="truncate">{m.name}</span>
                        </span>
                        <span className="text-2xs font-mono text-ink-muted flex-shrink-0">
                          ({m.brandNames.length})
                        </span>
                      </button>
                    );
                  })}
                  {selectedMfrIds.size > 0 && (
                    <>
                      <div className="border-t border-border-subtle my-1" />
                      <button
                        type="button"
                        onClick={() => setSelectedMfrIds(new Set())}
                        className="w-full text-left px-2 py-1 text-2xs text-ink-muted hover:text-ink-bright"
                      >
                        Clear selection
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Search */}
          <div className="relative ml-auto flex-shrink-0">
            <Search
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none"
            />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search title, SKU, brand…"
              aria-label="Search products by title, SKU, or brand"
              data-testid="buying-session-search"
              className="arbi-input pl-8 pr-3 py-1 text-xs w-60"
            />
          </div>
        </div>

        {/* Active filter chips — asymmetric per ADR Decision 44 */}
        {allChips.length > 0 && (
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {allChips.map((chip) => (
              <span
                key={chip.key}
                className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface-4 text-2xs text-ink-secondary"
              >
                {chip.label}
                {chip.source === 'manual' && (
                  <button
                    onClick={() => dismissChip(chip.key)}
                    aria-label={`Remove ${chip.label}`}
                    className="hover:text-ink-bright"
                  >
                    <X size={10} />
                  </button>
                )}
              </span>
            ))}
            {manualChips.length > 0 && (
              <button
                onClick={clearManualChips}
                className="text-2xs text-ink-muted hover:text-ink-primary ml-1"
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-2 bg-danger/10 border-b border-danger/30 text-sm text-danger flex-shrink-0">
          <AlertCircle size={14} />
          {error}
        </div>
      )}

      {/* ── Main layout: params | grid+tray | detail ────────── */}
      <div className="flex flex-1 overflow-hidden p-2 gap-2">
        {/* Left: Params sidebar */}
        <ParamsSidebar
          open={paramsPanelOpen}
          onToggle={() => setParamsPanelOpen((v) => !v)}
          filters={filters}
          weights={weights}
          constraints={constraints}
          onFiltersChange={handleFiltersChange}
          onWeightsChange={handleWeightsChange}
          onConstraintsChange={handleConstraintsChange}
          guardrails={guardrails}
          onGuardrailsChange={(k, v) => setGuardrails((s) => ({ ...s, [k]: v }))}
          risk={riskConfig}
          onRiskFactorToggle={handleRiskFactorToggle}
          onRiskThresholdChange={handleRiskThresholdChange}
          onResetFilters={handleResetFilters}
          onResetWeights={handleResetWeights}
          onResetAll={handleResetAll}
          onSavePreset={handleSavePreset}
          onSaveDefaults={handleSaveDefaults}
          scenarios={scenarios}
          scenariosError={scenariosError}
          // Day 73 P4.1 BACKLOG L427 — soft drift: displayedScenarioId is
          // null when state has drifted from the active scenario. Underlying
          // activeScenarioId state is unchanged + persisted; the picker
          // shows "Custom (none)" visually until state matches again.
          activeScenarioId={displayedScenarioId}
          onApplyScenario={handleApplyScenario}
          tierMode={{ enabled: tierMode.enabled, import_id: tierMode.import_id }}
          onTierModeChange={(patch) => {
            setTierMode((prev) => {
              const next = { ...prev };
              if (patch.enabled !== undefined) next.enabled = patch.enabled;
              if (patch.import_id !== undefined) next.import_id = patch.import_id;
              // Disabling tier mode: clear results so columns drop cleanly.
              if (patch.enabled === false) setTierResults(null);
              return next;
            });
          }}
          onOpenTierConfig={() => setTierConfigOpen(true)}
          algopixImports={algopixImports}
          selectedPath={selectedPath}
        />

        {/* Center: AG Grid + PO Tray */}
        <div className="flex-1 flex flex-col overflow-hidden gap-2">
          {/* Day 80 P5.5 F46 + L588 — error strip. Surfaces session load
              failures + writeback retry-once final-failures. Reload triggers
              full page reload since auto-load fires on [sessionId] hook
              deps — smaller blast radius than wiring a refetch helper. */}
          {sessionError && (
            <div
              role="alert"
              className="flex items-center gap-2 px-3 py-1.5 bg-warning/10 border border-warning/30 rounded-md text-xs text-warning flex-shrink-0"
              data-testid="buying-session-error-strip"
            >
              <span className="flex-1">
                {sessionError === 'Session not found'
                  ? 'Session not found. Check the URL or start a new buying session.'
                  : 'Session changes could not be saved. Check your connection and retry.'}
              </span>
              <button
                onClick={() => window.location.reload()}
                className="text-warning underline-offset-2 hover:underline"
              >
                Reload
              </button>
            </div>
          )}
          {/* Bulk selection action bar — visible whenever rows are selected.
              Clicking Add to PO opens the multi-add modal; Clear deselects. */}
          {selectedGridRows.length > 0 && !isViewer && (
            <div className="flex items-center gap-3 px-3 py-1.5 bg-accent/10 border border-accent/30 rounded-md flex-shrink-0">
              <span className="text-xs text-accent font-medium">
                {selectedGridRows.length} selected
              </span>
              <button
                onClick={handleOpenMultiAdd}
                className="arbi-btn text-2xs bg-accent text-white hover:bg-accent-hover px-2.5 py-1 flex items-center gap-1"
                data-testid="buying-session-add-to-po"
              >
                <ShoppingCart size={11} />
                Add to PO
              </button>
              <button
                onClick={clearGridSelection}
                className="text-2xs text-ink-muted hover:text-ink-primary"
              >
                Clear selection
              </button>
            </div>
          )}

          {/* AG Grid */}
          <div className="flex-1" style={{ minHeight: 0 }}>
            <AgGridReact
              ref={gridRef}
              theme={gridTheme}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowModelType="infinite"
              cacheBlockSize={200}
              maxBlocksInCache={50}
              selectionColumnDef={SELECTION_COLUMN_DEF}
              rowSelection={{
                mode: 'multiRow',
                enableClickSelection: false,
                checkboxes: true,
                headerCheckbox: false,
              }}
              onGridReady={onGridReady}
              onRowClicked={onRowClicked}
              onSelectionChanged={handleSelectionChanged}
              animateRows={false}
              headerHeight={36}
              rowHeight={42}
              suppressCellFocus={true}
              // Day 58 — suppressColumnVirtualisation is an INITIAL grid
              // option (not runtime-mutable per
              // node_modules/ag-grid-community/dist/types/src/gridOptionsInitial.d.ts).
              // Set unconditionally true so tier-mode columns (added at
              // runtime via setGridOption('columnDefs', …)) actually render
              // in the DOM regardless of viewport width. At Buying Session
              // scale (≤20 columns), virtualization-off has no practical
              // perf cost.
              //
              // suppressHorizontalScroll stays gated on tier-mode-active.
              // Outside tier mode, columns fit the ~1180px Architecture Rule
              // 16 budget — no scroll needed. With tier mode on, base+6 tier
              // cols overflow the viewport — let the user scroll.
              suppressColumnVirtualisation={true}
              suppressHorizontalScroll={!(tierMode.enabled && selectedPath !== 'all')}
              overlayNoRowsTemplate='<span style="color:var(--ink-muted);font-size:13px">No products found. Click Recalculate to run the profitability engine.</span>'
              overlayLoadingTemplate='<span style="color:var(--ink-muted);font-size:13px">Loading…</span>'
            />
          </div>

          {/* Bottom: PO Tray */}
          <POTray
            open={poTrayOpen}
            onToggle={() => setPoTrayOpen((v) => !v)}
            items={poStaging.items}
            onUpdateQty={poStaging.updateQuantity}
            onRemoveItem={poStaging.removeItem}
            onClearAll={poStaging.clearAll}
            loading={poStaging.loading}
            currentSessionDistributorId={distributorId}
            constraints={constraints}
            guardrails={guardrails}
          />
        </div>

        {/* Right: Detail panel */}
        <DetailPanel
          open={detailPanelOpen}
          onToggle={() => setDetailPanelOpen((v) => !v)}
          product={selectedProduct}
          channels={selectedChannels}
          safetyCap={safetyCap}
          onAddToPO={handleAddToPO}
        />
      </div>

      {/* ── Multi-add modal ─────────────────────────────────────
          Single-channel mode: selectedPath header is fixed to the active tab.
          All Channels mode: renders an ArbiSelect so the user picks the
          selectedPath once for the whole batch. Sibling-backdrop pattern so
          a11y rules don't need the `<div onClick=...>` escape hatch. */}
      {multiAddOpen && !isViewer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <button
            type="button"
            onClick={handleCloseMultiAdd}
            aria-label="Close dialog"
            tabIndex={-1}
            className="absolute inset-0 bg-black/60 cursor-default"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Add selected products to purchase order"
            className="relative bg-surface-2 border border-border rounded-xl shadow-2xl w-[720px] max-w-[calc(100vw-40px)] max-h-[calc(100vh-80px)] flex flex-col"
          >
            <div className="px-5 py-4 border-b border-border-subtle flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-semibold text-ink-bright">
                  Adding to PO —{' '}
                  {selectedPath === 'all' ? 'All Channels' : pathLabel[multiAddPath] || 'Channel'}
                </h3>
                <span className="text-2xs text-ink-muted">
                  {multiAddDrafts.length} SKU{multiAddDrafts.length !== 1 ? 's' : ''}
                </span>
              </div>
              <button
                onClick={handleCloseMultiAdd}
                className="text-ink-muted hover:text-ink-primary"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            {/* Path selector — only in All Channels mode. Promoted to
                a primary input row with a Required affordance so users don't
                accidentally drop the whole batch onto seedPath's default
                (B-R2-5). Submit is gated on pathConfirmed below. */}
            {selectedPath === 'all' && (
              <div className="px-5 py-4 bg-accent/5 border-b border-accent/20 flex-shrink-0">
                <div className="flex items-center gap-2 mb-2">
                  <label
                    htmlFor="multi-add-path"
                    className="text-2xs uppercase tracking-wider text-ink-bright font-semibold"
                  >
                    Channel
                  </label>
                  <span className="text-2xs px-1.5 py-0.5 rounded bg-warning/15 text-warning font-medium">
                    Required
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div id="multi-add-path" className="w-60">
                    <ArbiSelect
                      value={multiAddPath}
                      onChange={(v) => {
                        setMultiAddMp(String(v));
                        setPathConfirmed(true);
                      }}
                      options={activePaths.map((p) => ({
                        value: p.path_key,
                        label: p.display_name,
                      }))}
                    />
                  </div>
                  <span className="text-xs text-ink-secondary">
                    {pathConfirmed
                      ? 'Applies to every selected SKU'
                      : 'Confirm a channel before adding to PO'}
                  </span>
                </div>
              </div>
            )}

            {/* Line items */}
            <div className="flex-1 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-surface-2 z-10">
                  <tr className="text-ink-muted border-b border-border-subtle">
                    <th className="px-3 py-2 text-left font-medium w-28">ISG SKU</th>
                    <th className="px-3 py-2 text-left font-medium">Title</th>
                    <th className="px-3 py-2 text-right font-medium w-20">Cost</th>
                    <th className="px-3 py-2 text-right font-medium w-20">ROI%</th>
                    <th className="px-3 py-2 text-center font-medium w-32">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {multiAddDrafts.map((d) => {
                    const roi = d.roiByPath[multiAddPath] ?? null;
                    const roiColor =
                      roi == null
                        ? 'text-ink-muted'
                        : Number(roi) > 0
                          ? 'text-success'
                          : 'text-danger';
                    return (
                      <tr key={d.productId} className="border-b border-border-subtle">
                        <td
                          className="px-3 py-2 text-accent"
                          style={{ fontFamily: "'JetBrains Mono', monospace" }}
                        >
                          {d.isgSku || '\u2014'}
                        </td>
                        <td
                          className="px-3 py-2 text-ink-bright truncate max-w-[220px]"
                          title={d.title ?? undefined}
                        >
                          {d.title || '\u2014'}
                          {d.wasExisting && (
                            <span className="ml-2 text-2xs text-accent">· already staged</span>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-right text-ink-primary">
                          {fmt$(d.cost)}
                        </td>
                        <td className={clsx('px-3 py-2 font-mono text-right', roiColor)}>
                          {fmtPct(roi)}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => handleMultiAddQtyChange(d.productId, d.quantity - 1)}
                              aria-label={`Decrease quantity for ${d.isgSku ?? 'item'}`}
                              className="w-5 h-5 flex items-center justify-center rounded bg-surface-4 border border-border text-ink-secondary hover:text-ink-primary"
                            >
                              <Minus size={10} />
                            </button>
                            <span className="w-8 text-center font-mono text-ink-bright">
                              {d.quantity}
                            </span>
                            <button
                              onClick={() => handleMultiAddQtyChange(d.productId, d.quantity + 1)}
                              aria-label={`Increase quantity for ${d.isgSku ?? 'item'}`}
                              className="w-5 h-5 flex items-center justify-center rounded bg-surface-4 border border-border text-ink-secondary hover:text-ink-primary"
                            >
                              <Plus size={10} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer actions */}
            <div className="px-5 py-3 border-t border-border-subtle flex justify-end gap-2 flex-shrink-0">
              <button
                onClick={handleCloseMultiAdd}
                disabled={multiAddSubmitting}
                className="arbi-btn-ghost text-xs"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmMultiAdd}
                disabled={
                  multiAddSubmitting ||
                  multiAddDrafts.length === 0 ||
                  (selectedPath === 'all' && !pathConfirmed)
                }
                className="arbi-btn-primary text-xs"
                data-testid="multi-add-confirm"
              >
                {multiAddSubmitting
                  ? 'Adding...'
                  : `Add ${multiAddDrafts.length} SKU${multiAddDrafts.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Preset modal — Day 54 Phase 4 */}
      {savePresetOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <button
            type="button"
            onClick={() => setSavePresetOpen(false)}
            aria-label="Close dialog"
            tabIndex={-1}
            className="absolute inset-0 bg-black/60 cursor-default"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Save current params as scenario"
            className="relative bg-surface-2 border border-border rounded-xl shadow-2xl w-[440px] max-w-[calc(100vw-40px)] flex flex-col"
          >
            <div className="px-5 py-4 border-b border-border-subtle flex items-center justify-between">
              <h3 className="text-sm font-semibold text-ink-bright">Save as Scenario</h3>
              <button
                onClick={() => setSavePresetOpen(false)}
                className="text-ink-muted hover:text-ink-primary"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label
                  htmlFor="save-preset-name-input"
                  className="block text-xs font-medium text-ink-secondary mb-1.5"
                >
                  Name
                </label>
                <input
                  ref={savePresetNameRef}
                  id="save-preset-name-input"
                  type="text"
                  value={savePresetName}
                  onChange={(e) => setSavePresetName(e.target.value)}
                  placeholder="e.g. Conservative, Q4 Push"
                  className="arbi-input w-full text-xs px-2 py-1.5"
                  data-testid="save-preset-name"
                />
              </div>

              <div>
                <label
                  htmlFor="save-preset-scope"
                  className="block text-xs font-medium text-ink-secondary mb-1.5"
                >
                  Scope
                </label>
                <div id="save-preset-scope" className="w-full">
                  <ArbiSelect<'current' | 'all'>
                    value={savePresetScope}
                    onChange={(v) => setSavePresetScope(v)}
                    options={[
                      {
                        value: 'current',
                        label: activeDistributor?.code
                          ? `${activeDistributor.code} only`
                          : 'Current distributor only',
                      },
                      { value: 'all', label: 'All distributors (global)' },
                    ]}
                    size="sm"
                  />
                </div>
              </div>

              {savePresetError && (
                <div className="text-xs text-danger" role="alert">
                  {savePresetError}
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t border-border-subtle flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setSavePresetOpen(false)}
                disabled={savePresetSubmitting}
                className="arbi-btn-ghost text-xs px-3 py-1.5 disabled:opacity-50"
                data-testid="save-preset-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  void handleSavePresetConfirm();
                }}
                disabled={savePresetSubmitting || !savePresetName.trim()}
                className="arbi-btn-primary text-xs px-3 py-1.5 disabled:opacity-50"
                data-testid="save-preset-confirm"
              >
                {savePresetSubmitting ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Day 58 — Tier Config modal */}
      <TierConfigModal
        open={tierConfigOpen}
        tiers={tierMode.tiers}
        onSave={(nextTiers) => {
          setTierMode((prev) => ({ ...prev, tiers: nextTiers }));
        }}
        onClose={() => setTierConfigOpen(false)}
      />

      {/* Save as Draft modal — Day 54 Phase 5 */}
      {saveAsDraftOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <button
            type="button"
            onClick={() => setSaveAsDraftOpen(false)}
            aria-label="Close dialog"
            tabIndex={-1}
            className="absolute inset-0 bg-black/60 cursor-default"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Save buying session as draft"
            className="relative bg-surface-2 border border-border rounded-xl shadow-2xl w-[440px] max-w-[calc(100vw-40px)] flex flex-col"
          >
            <div className="px-5 py-4 border-b border-border-subtle flex items-center justify-between">
              <h3 className="text-sm font-semibold text-ink-bright">Save as Draft</h3>
              <button
                onClick={() => setSaveAsDraftOpen(false)}
                className="text-ink-muted hover:text-ink-primary"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label
                  htmlFor="save-as-draft-name-input"
                  className="block text-xs font-medium text-ink-secondary mb-1.5"
                >
                  Name
                </label>
                <input
                  id="save-as-draft-name-input"
                  ref={saveAsDraftNameRef}
                  type="text"
                  value={saveAsDraftName}
                  onChange={(e) => setSaveAsDraftName(e.target.value)}
                  placeholder="e.g. Q4 Holiday Push, Mid-week Review"
                  className="arbi-input w-full text-xs px-2 py-1.5"
                  data-testid="save-as-draft-name"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && saveAsDraftName.trim() && !saveAsDraftSubmitting) {
                      void handleSaveAsDraftConfirm();
                    }
                  }}
                />
                <p className="text-2xs text-ink-muted mt-1.5">
                  Drafts retain all filters, weights, constraints, scenarios, and column layout.
                  Reopen by URL — restoration is the same as resuming any active session.
                </p>
              </div>

              {saveAsDraftError && (
                <div className="text-xs text-danger" role="alert">
                  {saveAsDraftError}
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t border-border-subtle flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setSaveAsDraftOpen(false)}
                disabled={saveAsDraftSubmitting}
                className="arbi-btn-ghost text-xs px-3 py-1.5 disabled:opacity-50"
                data-testid="save-as-draft-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  void handleSaveAsDraftConfirm();
                }}
                disabled={saveAsDraftSubmitting || !saveAsDraftName.trim()}
                className="arbi-btn-primary text-xs px-3 py-1.5 disabled:opacity-50"
                data-testid="save-as-draft-confirm"
              >
                {saveAsDraftSubmitting ? 'Saving…' : 'Save Draft'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast — Day 54 Phase 4, COMPONENT_STANDARDS.md §6; Day 81.5 P1 unified */}
      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
